/**
 * @file src/ui/react/studio/MultiStageStudio.tsx
 * @description The multi-stage Orbiter Studio layout (decisions/0004) — the React shell that
 * generalizes the single-orbiter Studio from ONE stage to 1–4 **resizable** stages over the shared realm,
 * with the collection **arrange UX**: a card drawer, drag-to-stage, reorder, and per-stage remove. It owns
 * REAL DOM stages whose rects the `ViewportCompositor` reads every frame (geometry for free — resizing a
 * stage re-frames its voice with no messaging, no reload). It stays voice-agnostic: it emits stage-index
 * intents (focus, subtype change, load/clear/reorder, persist) and the boot wiring (`createCollectionApp`)
 * maps them onto realm voices.
 *
 * Permission model: **viewing grants full interaction.** The drawer, drag-to-stage, rearrange, remove, and
 * reorder are available to EVERYONE (their handlers are always supplied). Persisting the arrangement is the
 * only edit-gated action, and (like root) it happens AUTOMATICALLY — there is no Save button. The count
 * switcher + resize are view controls.
 *
 * The UX mirrors root's mixed-collection (`bottom-bar` + `card-drawer` + `orbiter-grid`) but is built in
 * React (the app's UI direction) with a lightweight POINTER drag (orbiters has no dnd-kit; the same 8/16px
 * activation distances). The pure geometry lives in `multi/stageGeometry.js`; the pure arrangement math in
 * `multi/stageArrangement.js`. Active-stage corner chrome is NOT drawn here for a FILLED stage: each voice's
 * own React UI already renders its `FourCornerCard` focus frame when it is the active voice.
 *
 * Two React roots coexist per stage: THIS root renders the empty stage `content` div; the voice's own
 * `OrbitersUI` root (mounted by `makeOrbiterVoiceSession` into that div) fills it. This root never gives the
 * content div children, so the two never reconcile the same nodes — the same pattern the vanilla grid used.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, ChevronDown, ChevronUp, SlidersHorizontal, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  BottomNavBar,
  Button,
  CornerButton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  FourCornerCard,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useIsMobileNav,
  type ScoreHeaderTitle,
} from 'plantasia.space-design/react';
import { Icon, IconProvider } from 'plantasia.space-design/icons';
import 'plantasia.space-design/styles.css';
import './MultiStageStudio.css';
// The collection shell is a SEPARATE React root (not under a mounted OrbitersUI), and collection
// mode STARTS EMPTY (no voice → orbitersUI.css not yet imported). Import it here so the `.orb-studio__
// portal-surface` chrome-token mapping (+ the `.multi-orbiter-cell` size-container rule) is present for the
// whole shell lifecycle — the portalled dock menus and the stage overlays read that mapping from mount.
import '../orbitersUI.css';
import { CardDrawer, HAND_CARD_EXIT_MS, type DrawerEntry } from './CardDrawer';
import { LocalFilesPanel } from './LocalFilesPanel';
import { OfflineDownloadControl } from './OfflineDownloadControl';
import { useOfflineCollection } from './useOfflineCollection';
import { useOfflineDownloadIntent } from './useOfflineDownloadIntent';
import { StartRoomDialog } from './StartRoomDialog';
import { StudioHeader, TOP_CHROME_VAR } from './StudioHeader';
import { LocalMissingDialog } from './LocalRailDialogs';
import { dragCarriesFiles, type LocalRailProps } from './localRail';
import { isLocalEntryId } from '../../../local/localAudioIds.js';
import notifications from '../../../core/AppNotifications.js';
import { StationTrackCard, StationTrackCover, type StationCardEntry } from './StationTrackCard';
import { entityMeta } from './entityMeta';
import { useControlTooltipRegistration } from '../regions/useControlTooltips';
import { toggleShellFullscreen } from '../regions/headerActions';
import { DeckHolderFaces } from '../regions/DeckHolders';
import { getT } from '../../../i18n/index.js';

// The filterable card kinds of a mixed collection, in drawer-filter order.
type EntityKind = 'track' | 'orbiter' | 'world';
const ENTITY_KINDS: EntityKind[] = ['track', 'orbiter', 'world'];

/** Classify a raw `entityType` into a filterable kind (null = unknown — never filtered out). */
function entityKindOf(entityType: string | null | undefined): EntityKind | null {
  return entityMeta(entityType).kind;
}
import { getHerbariumBase } from '../../../utils/cdnAssets.js';
import {
  applyStudioChromeTheme,
  clearStudioChromeTheme,
  prewarmStudioChromeTheme,
  resetStudioChromeThemeCache,
} from '../../studioChromeTheme.js';

// The stage-count tab glyph — root's mixed-collection bottom bar uses the ringed-planet `OrbiterIcon`
// (the design-lib entity `Icon name="orbiter"`). A thin wrapper so it slots into the dock `CornerButton`'s
// `icon` slot. Sized to 18px to match the dock's lucide icons: CornerButton's `[&_svg]:size-[18px]` sizes a
// raw svg, but this `<Icon>` wraps its svg in a `size-4` span, so the span is set explicitly here. Bytes
// resolve from the Herbarium CDN via the `IconProvider` wrapping the studio tree.
const OrbiterGlyph = ({ className }: { className?: string }) => (
  <Icon name="orbiter" className={className} style={{ width: 18, height: 18 }} aria-hidden />
);

import {
  SLOT_LABELS,
  clampRatio,
  clampSubtype,
  defaultSubtype,
  dividersFor,
  slotStylesFor,
  snapRatio,
  LAYOUT_TRANSITION_MS,
} from '../../../multi/stageGeometry.js';

// The card rail is a horizontal carousel AND the place drag-to-load cards start, so the two gestures must
// coexist WITHOUT the browser reclaiming a touch mid-drag: a native pan-x scroll fires `pointercancel` and
// drops the card before it reaches a slot. So the cards are `touch-action: none` (see CardDrawer) — the
// browser never scrolls or cancels them — and WE own the whole gesture. On the first few px of travel we
// lock an axis: a mostly-HORIZONTAL move scrolls the rail (we drive `scrollLeft` to follow the finger); a
// mostly-VERTICAL touch — or any mouse/pen move — lifts the card out to a placeholder and, because nothing
// can cancel it, tracks the finger all the way to any slot. No long-press, no premature drop.
const DRAG_AXIS_LOCK_DISTANCE = 8;
// A double-tap / double-click on a card loads it into the ACTIVE orbiter — the reliable, device-independent
// load path that does not depend on the drag working (loading a track is the critical action; drag feel
// varies across touch hardware). Two taps on the same card within this window count as one.
const DOUBLE_TAP_MS = 320;
// Carousel fling: because we drive the rail scroll ourselves (cards are touch-action:none), a release has
// no native momentum — so we coast. On release we take the finger's scroll velocity (px/ms) and decay it
// each frame by FLING_FRICTION (per ~16ms, frame-rate independent), stopping below FLING_STOP_VELOCITY or
// at a scroll edge. Below FLING_MIN_VELOCITY a release is a gentle placement, not a flick — no coast.
const FLING_MIN_VELOCITY = 0.08; // px/ms — the flick threshold
const FLING_MAX_VELOCITY = 4; // px/ms — clamp spikes from a tiny-dt sample
const FLING_FRICTION = 0.94; // velocity retained per 16ms frame
const FLING_STOP_VELOCITY = 0.02; // px/ms — coast ends here
const FLING_VEL_SMOOTHING = 0.6; // EMA weight on the newest sample — damps a single end-of-swipe jitter
const FLING_SAMPLE_MAX_AGE = 60; // ms — if the last move is older than this at release, the finger had
                                 // stopped: don't fling (a held-then-lifted swipe should settle, not coast)

const SLOT_TRANSITION = `left ${LAYOUT_TRANSITION_MS}ms cubic-bezier(0.22,1,0.36,1), top ${LAYOUT_TRANSITION_MS}ms cubic-bezier(0.22,1,0.36,1), width ${LAYOUT_TRANSITION_MS}ms cubic-bezier(0.22,1,0.36,1), height ${LAYOUT_TRANSITION_MS}ms cubic-bezier(0.22,1,0.36,1)`;

// Mobile: a vertical stack (no resize) that is NOT user-scrollable — the scroll container is
// pointer-events:none so a swipe/drag reaches the orbiter's camera; the prev/next bar buttons move between
// orbiters via programmatic `scrollIntoView({behavior:'smooth', block:'start'})`, which gives the slide
// transition. Each stage is a FULL screen, so scrollIntoView lands exactly one orbiter per view. Ported
// from root's mobile branch (a persistent nav bar to page + toggle the cards).
// The studio header (the session's name, and the score beside it in a scored room) is a header, not
// an overlay: it publishes its own height (see `StudioHeader`) and the studio reserves that strip,
// exactly as it already does for the bottom nav bar and the mobile drawer. A stage is still a FULL
// screen — of what is left below the bar — so paging still lands one orbiter per view instead of
// scrolling a screen and a half. Zero where there is no header (an inline roster).
const TOP_CHROME_INSET = `var(${TOP_CHROME_VAR}, 0px)`;
// The local-files panel's own strip, published by the panel and reserved by the studio root, so the
// stages reflow beside it instead of sitting underneath it. Zero whenever it is closed.
const LOCAL_PANEL_INSET = 'var(--orbiters-local-panel, 0px)';
const MOBILE_STAGE_HEIGHT = `calc(var(--viewport-max-height, 100dvh) - ${TOP_CHROME_INSET})`;
// The mobile chrome IS the design-lib BottomNavBar (same component as desktop): portrait = a fixed bottom
// bar of height `--nav-mobile-bar-height`; landscape = the lib's fixed LEFT rail (w-16 = 64px), matching
// root. Stages + the drawer reserve that strip so nothing hides behind the bar.
const MOBILE_BAR_INSET = 'var(--nav-mobile-bar-height, 4rem)'; // portrait: the bottom bar's reserved strip
const MOBILE_RAIL_W = '4rem'; // landscape: the left rail's width — matches the lib rail's `w-16` (4rem)
                              // exactly regardless of root font-size (a px literal would drift under large-text)
// Mobile drawer is a FIXED reduced height (no resize): unlike desktop's resizable drawer, it REFLOWS the
// orbiter — the stage shrinks by this much while the drawer is open, so the cards and the full play area
// are both visible (never overlaid). Sized to the drawer's reduced card tier.
const MOBILE_DRAWER_H = 200; // px

export interface StudioLayoutState {
  subtype: number;
  splitY: number;
  splitXPrimary: number;
  splitXSecondary: number;
  focusedIndex: number;
  /** The card drawer along the bottom: whether it is showing, and how tall it was dragged. */
  drawerOpen: boolean;
  drawerHeight: number;
  // The local-files panel is NOT here. It shows this machine's own files, so how it was left is a
  // per-machine fact and is remembered with them (`localRail.onPanelChange`) — not in an arrangement
  // every viewer of the collection reads.
}

/** The part of the layout the STUDIO owns: how it was sized and what was left showing. The stage
 *  count and what sits on each stage are the app's — it is the one arranging them.
 *
 *  Both panel SIZES are desktop measurements in px. On a phone neither is used (the drawer takes a
 *  fixed reduced height and the panel is a full-width sheet), so a remembered size never arrives
 *  somewhere it cannot fit; both are clamped on the way in regardless. */
export type StudioGeometry = Omit<StudioLayoutState, 'subtype'>;

export interface MultiStageStudioHandle {
  /** The stage `index`'s content element — the compositor's viewport cell + the voice's UI mount. */
  acquireCell: (index: number) => HTMLElement | null;
  /** Reflect the realm's focused voice onto the layout (Roman highlight). */
  setActiveIndex: (index: number) => void;
  /** Reflect the realm's multi-SELECTION (shift-click / drag-paint) onto the layout. The orbiter map
   *  brackets every selected position, so the shell must mirror the set, not just the primary. */
  setSelectedIndices: (indices: number[]) => void;
  /** Focus the stage and run the full view-navigation path (used by MIDI autofocus on mobile). */
  focusIndex: (index: number) => void;
}

export interface MultiStageStudioProps {
  /** How many front stages hold real content (fill front-to-back; the rest show an empty placeholder). */
  rosterLength: number;
  savedLayout?: Partial<StudioLayoutState> | null;
  onReady?: (handle: MultiStageStudioHandle) => void;
  onFocusStage?: (index: number, additive?: boolean, fromMidi?: boolean) => void;
  /** The geometry SETTLED — a divider drag ended somewhere new, or the user deliberately focused a
   *  stage. Called with all four fields, so the receiver stores one shape rather than merging deltas.
   *
   *  Settle points only, and that is the whole point of this seam: a drag is a burst of frames and
   *  focus moves on its own every time a card lands on a stage (the realm reflects that through
   *  `setActiveIndex`, which does NOT call this). Persisting either of those as it happened would be
   *  writing noise. What is reported is what the user chose and stopped at. */
  onGeometryChange?: (geometry: Partial<StudioGeometry>) => void;
  onSubtypeChange?: (next: number, prev: number) => void;
  /** Leave the collection Studio (the persistent nav's "Go back"). */
  onBack?: () => void;
  /** Cruise mode: the software loads and plays tracks one after another — Traktor-style. Absent =
   *  the control is not offered (normal playback never auto-advances). */
  cruise?: { enabled: boolean; onToggle: () => void } | null;
  /** "Load full sessions" (the drawer toolbar): when engaged, a drop replaces the deck with the
   *  card's ORIGINAL session instead of swapping only the card's own dimension. */
  loadDefaults?: { enabled: boolean; onToggle: () => void } | null;
  /** Where MIDI mappings are saved and loaded: on = this collection (per stage), off = the orbiter. */
  midiScope?: { enabled: boolean; onToggle: () => void } | null;
  /** True when no entry in the collection can start a stage — only a track can, and this one has
   *  none. The studio says so up front (see the notice dialog) instead of leaving the user with
   *  stages that silently refuse every card. */
  noPlayableEntries?: boolean;
  // Arrange UX. These are supplied to EVERYONE who can view (viewing grants full session-local
  // interaction); only Save (persist) is edit-gated.
  /** The collection's entries, listed in the drawer as draggable cards. */
  entries?: DrawerEntry[];
  /** The live per-stage occupant, keyed by the SOURCE (drawer) voiceId — so the drawer can mark the active
   *  card (a placement's realm voiceId is minted and would never match a card). Index = stage; null = empty. */
  stageVoiceIds?: (string | null)[];
  /** The live per-stage entry metadata, keyed by stage index, for per-tile artwork chrome. */
  stageEntries?: (StationCardEntry | null)[];
  /** Per-stage real orbiter id owning that stage's slot-focus MIDI target's persistence (index =
   *  stage, matching `stageVoiceIds`) — the mapping is orbiter-owned like every other MIDI
   *  mapping (the backend has no collection-scoped identity), so it lives with whichever real
   *  orbiter currently occupies THAT slot, never a shared collection-wide anchor. */
  focusMidiPersistenceIds?: (string | null)[];
  /** Source (drawer) voiceIds with an in-flight drop/load — those cards show the orbit loader
   *  until the target voice reports its load cycle finished. */
  loadingSourceIds?: string[];
  /** Drop a drawer entry onto stage `index` (replace whatever is there). */
  onLoadStage?: (index: number, entry: DrawerEntry) => void;
  /** Clear stage `index` (the per-stage remove control). */
  onClearStage?: (index: number) => void;
  /** Reorder the drawer list: move `sourceVoiceId` before `beforeVoiceId` (null = to the end). */
  onReorderEntries?: (sourceVoiceId: string, beforeVoiceId: string | null) => void;
  /** Remove a card from the COLLECTION behind this session (the card's context menu). Unlike the
   *  arrange handlers this one writes to the collection, so the app supplies it only to a viewer who
   *  may edit it — absent/null means the menu offers no remove at all. */
  onRemoveEntry?: ((sourceVoiceId: string) => void) | null;
  /** i18n key for the remove label — a card leaves a COLLECTION or the user's QUEUE. */
  removeEntryLabelKey?: string;
  /** The section's dealt HAND — the source voiceIds the card rail may show, and the only cards a drag
   *  may deal. A scored room deals a few cards per section rather than offering the whole collection.
   *  Null = no hand (an unscored room, or a session that is not a room), and the rail shows everything
   *  as before. Applied at RENDER level only: `entries` itself is never narrowed, because remote deck
   *  commands and the late-join deck snapshot resolve their cards through that list, and a deck can
   *  still be holding a card from an earlier hand. */
  handSourceIds?: string[] | null;
  /** Cards the hand dealt that the collection could not resolve — shown unavailable rather than
   *  dropped, so both players count the same number of cards. */
  handMissingEntries?: DrawerEntry[];
  /** Cards of the hand that have already been PLAYED — they leave the rail and the remaining ones
   *  close up, so the hand shows only what is still there to play. Removing the card is a rail
   *  change and nothing more: the orbiter it was dealt onto keeps playing. */
  handConsumedIds?: string[];
  registerMidiTarget?: (binding: {
    id: string;
    element: HTMLElement;
    componentId: string;
    componentType: 'kick';
    scope: 'GLOBAL';
    /** The binding's persistence slice: the owning orbiter for track-focus targets, the
     *  collection for shell targets. */
    persistenceScope?: { scope: string; entityId: string } | null;
    onTrigger: () => void;
  }) => void;
  unregisterMidiTarget?: (id: string) => void;
  /** Non-null enables the shell MIDI targets (slot focus/add, pager, drawer), persisted under
   *  THIS collection — the studio's own actions, independent of what's loaded in any slot. */
  shellMidiCollectionId?: string | null;
  /** The loaded collection id — enables the in-app "Start room" affordance (mints a room grant for
   *  this collection). Null/absent (queue / inline roster) hides it. */
  collectionId?: string | null;
  /** The collection's own name. Display only, and stored with a download so the offline list can
   *  name it with no connection to ask. */
  collectionName?: string | null;
  /** What the header names this session — the collection, "your queue". Null (an inline roster)
   *  shows no header, and the leave action stays in Options where it can still be reached. */
  headerTitle?: string | null;
  /** True when this session is ALREADY a room — the "Start room" affordance is then hidden. */
  roomActive?: boolean;
  /** The room's fixed DECK count (the room shape, chosen at creation). In a room this — not the
   *  collection/pool size — fixes the visible stage count (the pool is any size; the layout is the
   *  decks). Ignored outside a room. */
  roomDeckCount?: number | null;
  /** The local audio rail (files on this machine), when this session offers one. Null = not offered. */
  localRail?: LocalRailProps | null;
}

const ratioOrDefault = (v: number | undefined) => (Number.isFinite(v) ? clampRatio(v as number) : 0.5);

type Ratio = 'y' | 'xPrimary' | 'xSecondary';

/**
 * The geometry fields a stored layout actually STATES. A layout written before a field existed says
 * nothing about it, and that silence has to survive: the studio reads an absent field as its own
 * default, so carrying a default back into the record would turn "never set" into a stated choice.
 */
function statedGeometry(layout?: Partial<StudioLayoutState> | null): Partial<StudioGeometry> {
  const out: Partial<StudioGeometry> = {};
  if (!layout) return out;
  if (Number.isFinite(layout.splitY)) out.splitY = layout.splitY;
  if (Number.isFinite(layout.splitXPrimary)) out.splitXPrimary = layout.splitXPrimary;
  if (Number.isFinite(layout.splitXSecondary)) out.splitXSecondary = layout.splitXSecondary;
  if (Number.isFinite(layout.focusedIndex)) out.focusedIndex = layout.focusedIndex;
  if (typeof layout.drawerOpen === 'boolean') out.drawerOpen = layout.drawerOpen;
  if (Number.isFinite(layout.drawerHeight)) out.drawerHeight = layout.drawerHeight;
  return out;
}

/** Which stored field each divider writes. */
const RATIO_FIELD: Record<Ratio, keyof StudioGeometry> = {
  y: 'splitY',
  xPrimary: 'splitXPrimary',
  xSecondary: 'splitXSecondary',
};

type PendingConfirmation = { kind: 'back' } | { kind: 'shrink'; next: number; dropped: number } | null;

/** Live `(orientation: landscape)` — reactive on rotate. Combined with `useIsMobileNav` for mobile-landscape. */
function useOrientationLandscape(): boolean {
  const [landscape, setLandscape] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(orientation: landscape)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(orientation: landscape)');
    const onChange = () => setLandscape(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return landscape;
}

export function MultiStageStudio({
  rosterLength,
  savedLayout = null,
  onReady,
  onFocusStage,
  onGeometryChange,
  onSubtypeChange,
  onBack,
  cruise = null,
  loadDefaults = null,
  midiScope = null,
  noPlayableEntries = false,
  entries,
  stageVoiceIds,
  stageEntries,
  focusMidiPersistenceIds,
  loadingSourceIds,
  onLoadStage,
  onClearStage,
  onReorderEntries,
  onRemoveEntry = null,
  removeEntryLabelKey,
  handSourceIds = null,
  handMissingEntries,
  handConsumedIds,
  registerMidiTarget,
  unregisterMidiTarget,
  shellMidiCollectionId = null,
  collectionId = null,
  collectionName = null,
  headerTitle = null,
  roomActive = false,
  roomDeckCount = null,
  localRail = null,
}: MultiStageStudioProps) {
  const t = getT();
  // OS files dragged over a stage: the designed "dropped on a panel" path. Desktop only, like the
  // rail's own drop target; a phone has no file drag.
  const [fileDropStage, setFileDropStage] = useState<number | null>(null);
  // A room can't share a file from this machine, so "Start room" refuses while one is on a stage —
  // visibly, with the reason, rather than minting a room the other player would find half empty.
  const stagedLocalCount = (stageVoiceIds ?? []).filter((id) => isLocalEntryId(id)).length;
  // In a room the visible stage count IS the room's fixed deck count (the pool can be any size); the
  // count switcher is hidden (roomActive), so this initial value is also the FIXED value. Outside a
  // room the saved subtype / roster length decides, exactly as before.
  const initialSubtype =
    roomActive && Number.isFinite(roomDeckCount)
      ? clampSubtype(roomDeckCount as number)
      : defaultSubtype(savedLayout, rosterLength);
  const [subtype, setSubtype] = useState(() => initialSubtype);
  // The in-app "Start room" dialog (collection sessions that aren't already a room). Opened deferred
  // from the collection menu — like the shrink/leave confirm — so two Radix modals never transition in
  // the same tick and wedge the body pointer-events lock.
  const [startRoomOpen, setStartRoomOpen] = useState(false);
  const canStartRoom = Boolean(collectionId) && !roomActive;
  const [splitY, setSplitY] = useState(() => ratioOrDefault(savedLayout?.splitY));
  const [splitXPrimary, setSplitXPrimary] = useState(() => ratioOrDefault(savedLayout?.splitXPrimary));
  const [splitXSecondary, setSplitXSecondary] = useState(() => ratioOrDefault(savedLayout?.splitXSecondary));
  const [activeIndex, setActiveIndex] = useState(() => {
    const focused = Number.isFinite(savedLayout?.focusedIndex) ? (savedLayout!.focusedIndex as number) : 0;
    // Clamp into range: a malformed/legacy savedLayout could carry a focusedIndex past its stage count,
    // which the mobile pager (activeIndex ± 1) would otherwise strand on a non-existent stage.
    return Math.min(Math.max(0, focused), initialSubtype - 1);
  });
  const [resizing, setResizing] = useState(false);
  // The card drawer starts SHOWING unless this viewer left it closed — a studio whose cards are out
  // of the way is a deliberate state, and coming back to a drawer you had put away is the point.
  const [drawerOpen, setDrawerOpen] = useState(() => savedLayout?.drawerOpen !== false);
  // Managing the files on this machine — a different question from the rail below, so its own
  // surface, and closed until asked for. How this MACHINE left it arrives with the local library
  // (see the restore effect below), not from the arrangement: the panel shows files only this
  // machine has, so a viewer must never inherit the editor's open panel and an empty list.
  const [localPanelOpen, setLocalPanelOpen] = useState(false);
  // The two panel SIZES. Each panel owns its own size while it is being dragged and reports where it
  // settled; the studio holds the last reported value only so it can be handed on to be remembered,
  // and passes it back as the size to open at next time. One owner for the size, one for the memory.
  const [drawerHeight, setDrawerHeight] = useState<number | null>(() =>
    Number.isFinite(savedLayout?.drawerHeight) ? (savedLayout!.drawerHeight as number) : null,
  );
  const [localPanelWidth, setLocalPanelWidth] = useState<number | null>(null);
  // The dock's orbiter map (which card sits in which slot) — a drop-up off the monitor button.
  const [mapOpen, setMapOpen] = useState(false);
  // The realm's multi-selection, mirrored for the map's brackets. Empty means "no multi-selection" —
  // the primary alone is selected, which the map derives from `activeIndex` rather than storing twice.
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation>(null);
  // The "nothing here can play" notice is shown once, on arrival, and stays dismissed for the
  // session — the collection's contents can't be changed from inside the Studio, so repeating it
  // would only be in the way.
  const [noticeDismissed, setNoticeDismissed] = useState(false);

  const gridRef = useRef<HTMLDivElement | null>(null);
  // The mobile vertical stage stack's scroll container (this component's own root). The pager scrolls IT
  // directly (below) rather than relying on `Element.scrollIntoView`, which resolves + smooth-scrolls this
  // fixed, pointer-events:none container unreliably on mobile engines.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  // Wire the whole studio chrome into the app-wide control-tooltips system (the flag the orbiter
  // More menu — and the drawer's own toggle — flips): every button under the studio root gets a
  // hover/tap helper from its aria-label, exactly like the orbiter play-UI controls. The studio is
  // its own React root, so it must register itself — the orbiter UI's registration can't see it.
  useControlTooltipRegistration(scrollContainerRef);
  const scrollRafRef = useRef<number | null>(null); // in-flight pager scroll-tween (cancel on retarget/unmount)
  const stageRefs = useRef<Map<number, HTMLElement>>(new Map());
  const prevSubtypeRef = useRef(subtype);
  const dragRef = useRef<{
    move?: (e: PointerEvent) => void;
    up?: (e: PointerEvent) => void;
    cancel?: (e: PointerEvent) => void;
  }>({});

  // ── Chrome theme ─────────────────────────────────────────────────────────────────────────────────
  // The collection SHELL — dock (bottom bar) + card drawer/cards + the stage overlays (placeholders,
  // A/B/C/D badges, remove, artwork) — is CHROME: it follows the USER chrome theme from `/me/users/settings`,
  // exactly like the single-orbiter edit inspector (studioChromeTheme.js / StudioShell.tsx). It must NOT
  // touch the orbiter play UI inside each `.multi-orbiter-cell`, which keeps its own entity theme (the
  // `.orbiters-react-ui` bridge). So `applyStudioChromeTheme` writes the resolved preset tokens INLINE onto
  // the dock + drawer containers only (never a cell ancestor) and mirrors them onto <body> as
  // `--orb-studio-chrome-*`; the scattered stage overlays + portalled menus read that mirror via
  // `.orb-studio__portal-surface`. Resolve once up front (cache warm → applies synchronously, no flash).
  const chromeTargets = useRef<Set<HTMLElement>>(new Set());
  const chromeRef = useCallback((el: HTMLElement | null) => {
    if (!el) return undefined; // React 19 calls the returned cleanup on unmount, so this branch is inert
    chromeTargets.current.add(el);
    void applyStudioChromeTheme(el).catch(() => {});
    return () => {
      chromeTargets.current.delete(el);
      // `clearStudioChromeTheme` also clears the GLOBAL <body> portal mirror, so only run it once the LAST
      // chrome surface is gone — a dock/drawer swap on a breakpoint change must not strip the mirror from a
      // still-mounted sibling. An unmounting element takes its own inline vars with it, so nothing else needs
      // clearing on a non-last unmount.
      if (chromeTargets.current.size === 0) clearStudioChromeTheme(el);
    };
  }, []);
  useEffect(() => {
    prewarmStudioChromeTheme();
  }, []);
  // Embedded auth arrives AFTER mount (loaders.js dispatches `orbiters:auth-token`), so the first resolve
  // saw no settings and painted the DEFAULT preset — drop it, re-resolve the user's real preset once, and
  // re-apply to whichever chrome surfaces are currently mounted (dock + drawer).
  useEffect(() => {
    const onAuth = () => {
      resetStudioChromeThemeCache();
      chromeTargets.current.forEach((el) => void applyStudioChromeTheme(el).catch(() => {}));
    };
    document.addEventListener('orbiters:auth-token', onAuth);
    return () => document.removeEventListener('orbiters:auth-token', onAuth);
  }, []);

  // Stable ref callback per stage index — an inline `ref={el => …}` gets a new identity every render, so
  // React would detach+reattach it each time (briefly nulling the compositor's stored cell). Memoized
  // callbacks only fire on real mount/unmount.
  const stageRefCbs = useRef<Map<number, (el: HTMLElement | null) => void>>(new Map());
  const getStageRefCb = (index: number) => {
    let cb = stageRefCbs.current.get(index);
    if (!cb) {
      cb = (el: HTMLElement | null) => {
        if (el) stageRefs.current.set(index, el);
        else stageRefs.current.delete(index);
      };
      stageRefCbs.current.set(index, cb);
    }
    return cb;
  };

  // Stable per-index BLOCK refs (the scrollable mobile stage container) for prev/next scroll-into-view.
  const blockRefs = useRef<Map<number, HTMLElement>>(new Map());
  const blockRefCbs = useRef<Map<number, (el: HTMLElement | null) => void>>(new Map());
  const getBlockRefCb = (index: number) => {
    let cb = blockRefCbs.current.get(index);
    if (!cb) {
      cb = (el: HTMLElement | null) => {
        if (el) blockRefs.current.set(index, el);
        else blockRefs.current.delete(index);
      };
      blockRefCbs.current.set(index, cb);
    }
    return cb;
  };

  // ── Reporting the geometry ───────────────────────────────────────────────────────────────────────
  // What the geometry SETTLED at: seeded from the arrangement this session opened on, and changed only
  // by the reporters below.
  //
  // Deliberately NOT a mirror of the live React state. A split ratio moves on every frame of a drag, so
  // a report from somewhere else — a stage focused, a panel toggled — landing mid-drag would carry an
  // intermediate size the user never let go of, and the debounce would write it.
  //
  // And a field ABSENT here is a field nobody has ever set. It stays absent from the report: the studio
  // reads a missing value as its own default, so sending that default instead would file it as an
  // answer the user gave. For the drawer, which shows by default, that is the difference between
  // "never touched" and "deliberately left showing".
  const settledGeometryRef = useRef<Partial<StudioGeometry>>(statedGeometry(savedLayout));
  const onGeometryChangeRef = useRef(onGeometryChange);
  onGeometryChangeRef.current = onGeometryChange;
  // The local rail as it stands, for the callbacks that must not be rebuilt when it changes.
  const localRailRef = useRef(localRail);
  localRailRef.current = localRail;
  const onFocusStageRef = useRef(onFocusStage);
  onFocusStageRef.current = onFocusStage;

  /** Record what just settled and hand the whole shape on. Stable, so the MIDI registrations that
   *  depend on `focusStage` are not torn down and rebuilt on every render. */
  const reportGeometry = useCallback((changed: Partial<StudioGeometry>) => {
    const next = { ...settledGeometryRef.current, ...changed };
    settledGeometryRef.current = next;
    onGeometryChangeRef.current?.(next);
  }, []);

  /** Focus a stage because the USER said so — the orbiter map, the pager, a MIDI focus trigger. That
   *  is the focus worth remembering, and the only one that reaches here: the realm's own reflection of
   *  which voice is active (`setActiveIndex` on the handle) fires on every card drop and stays out of it.
   *
   *  An ADDITIVE click builds a multi-selection rather than moving the focused stage, so it is passed on
   *  untouched and reports nothing. */
  /** Show or hide the card drawer, and remember which. A deliberate act every time — the only thing
   *  that ever moves it is the Cards toggle or a drag of its handle all the way down. */
  const setDrawerShown = useCallback(
    (next: boolean) => {
      setDrawerOpen(next);
      reportGeometry({ drawerOpen: next });
    },
    [reportGeometry],
  );

  /**
   * Same for the local-files panel down the right — but remembered with the FILES rather than with
   * the arrangement, because the panel shows this machine's own and nobody else's.
   */
  const panelRestored = useRef(false);
  const setLocalPanelShown = useCallback(
    (next: boolean) => {
      // Whatever the stored value turns out to be, the person has now said what they want.
      panelRestored.current = true;
      setLocalPanelOpen(next);
      localRailRef.current?.onPanelChange?.({ open: next });
    },
    [],
  );

  /** Each panel reports where its own resize drag came to rest; the studio remembers the number. */
  const handleDrawerHeightSettled = useCallback(
    (height: number) => {
      setDrawerHeight(height);
      reportGeometry({ drawerHeight: height });
    },
    [reportGeometry],
  );
  const handleLocalPanelWidthSettled = useCallback(
    (width: number) => {
      panelRestored.current = true;
      setLocalPanelWidth(width);
      localRailRef.current?.onPanelChange?.({ width });
    },
    [],
  );

  /**
   * Put the panel back the way this machine left it, once, when the local library says how that was.
   *
   * The studio mounts before the stored record has been read, so it opens closed and adopts the
   * answer when it lands — `panelKnown` is what tells "not read yet" from a deliberate "closed".
   * Skipped entirely if the person has already touched the panel in the meantime: what they just
   * did outranks what they did last time.
   */
  useEffect(() => {
    if (panelRestored.current || !localRail?.panelKnown) return;
    panelRestored.current = true;
    if (localRail.panelOpen) setLocalPanelOpen(true);
    if (Number.isFinite(localRail.panelWidth)) setLocalPanelWidth(localRail.panelWidth as number);
  }, [localRail?.panelKnown, localRail?.panelOpen, localRail?.panelWidth]);

  const focusStage = useCallback(
    (index: number, additive = false, fromMidi = false) => {
      onFocusStageRef.current?.(index, additive, fromMidi);
      if (!additive) reportGeometry({ focusedIndex: index });
    },
    [reportGeometry],
  );

  const isMobile = useIsMobileNav();
  const isLandscapeOrientation = useOrientationLandscape();
  const isLandscape = isMobile && isLandscapeOrientation;
  // The MIDI-learn target is the TRACK's own artwork, not the stage position — a
  // learned mapping means "select THIS track", so it's scoped to that track's own real orbiter (see
  // the registration effect below), never a slot letter. (The artwork element itself is now
  // hidden — see getFocusArtworkRefCb's render — but stays mounted as this ref target.)
  const focusArtworkRefs = useRef<Map<number, HTMLElement>>(new Map());
  const focusArtworkRefCbs = useRef<Map<number, (el: HTMLElement | null) => void>>(new Map());
  const getFocusArtworkRefCb = (index: number) => {
    let cb = focusArtworkRefCbs.current.get(index);
    if (!cb) {
      cb = (el: HTMLElement | null) => {
        if (el) focusArtworkRefs.current.set(index, el);
        else focusArtworkRefs.current.delete(index);
      };
      focusArtworkRefCbs.current.set(index, cb);
    }
    return cb;
  };

  // Where the container must be scrolled for `block` to sit at the top of the CONTENT box — i.e. just
  // below the strip the studio reserves for the room's score bar. `offsetTop` is measured from the
  // container's PADDING box, so it already counts that strip, while `scrollTop` counts from the content
  // edge: scrolling straight to `offsetTop` rolls the reserved strip off the top and parks the stage
  // UNDER the bar. The first stage only ever looked right because arriving there for the first time
  // needs no scroll at all (`scrollTop` still 0) — coming back to it put it under the bar like the rest.
  const alignedScrollTop = (container: HTMLElement, block: HTMLElement) =>
    Math.max(0, block.offsetTop - (parseFloat(getComputedStyle(container).paddingTop) || 0));

  const revealStage = (target: number | null) => {
    if (target == null) return;
    setActiveIndex(target);
    // Scroll the OWNED container to the target block (mobile pager = one full-screen stage per view). We
    // drive the container directly instead of `block.scrollIntoView({behavior:'smooth'})`: that walks the
    // ancestor chain to find a scroll port and mobile engines resolve/animate this fixed, pointer-events:
    // none container unreliably — so the selection changed but the view never moved.
    //
    // Animate the scroll OURSELVES (a small rAF tween), not with `behavior:'smooth'`. The browser-smooth
    // animation was silently dropped by mobile engines whenever the `setActiveIndex` re-render reflowed
    // mid-flight (why the slide worked only SOMETIMES; nudging the current-orbiter button "fixed" it —
    // `setActiveIndex(sameValue)` bails the re-render, so nothing aborted the scroll). We re-assert
    // `scrollTop` every frame, so a reflow can cost at most one janky frame, never the whole slide; each
    // step fires a scroll event so the compositor re-reads the cell rects and the planet slides with it.
    // Target = the block's stable absolute top within this container (its offsetParent, since the root is
    // `position:fixed`), minus the reserved top strip — see `alignedScrollTop`.
    const container = scrollContainerRef.current;
    const block = blockRefs.current.get(target);
    if (!container || !block) return;
    if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current); // retarget cleanly
    const from = container.scrollTop;
    const dist = alignedScrollTop(container, block) - from;
    if (dist === 0) return;
    const DURATION = 300;
    let startTs: number | null = null;
    const step = (ts: number) => {
      if (startTs == null) startTs = ts;
      const p = Math.min(1, (ts - startTs) / DURATION);
      container.scrollTop = from + dist * (1 - (1 - p) ** 3); // easeOutCubic
      scrollRafRef.current = p < 1 ? requestAnimationFrame(step) : null;
    };
    scrollRafRef.current = requestAnimationFrame(step);
  };

  const navigateToStage = (target: number | null) => {
    if (target == null) return;
    focusStage(target);
    revealStage(target);
  };

  // A stable handle for the boot wiring. The setters are re-pointed each render so they always call
  // the latest state setter without rebuilding the handle (the realm holds this reference for its life).
  const setActiveIndexRef = useRef(setActiveIndex);
  setActiveIndexRef.current = setActiveIndex;
  const setSelectedIndicesRef = useRef(setSelectedIndices);
  setSelectedIndicesRef.current = setSelectedIndices;
  const handleRef = useRef<MultiStageStudioHandle>({
    acquireCell: (index: number) => stageRefs.current.get(index) ?? null,
    setActiveIndex: (index: number) => setActiveIndexRef.current(index),
    setSelectedIndices: (indices: number[]) => setSelectedIndicesRef.current(indices),
    focusIndex: (index: number) => revealStage(index),
  });

  // Announce readiness ONCE the initial stages are committed (their refs are populated) so the realm can
  // boot and `acquireCell` finds real DOM — the follow-up-commit timing the portalled bar also needs.
  useEffect(() => {
    onReady?.(handleRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // In a room the deck count is not ours to keep: the relay states the room's shape when we join,
  // and everyone adopts the same pair — which is what stops two people with differently edited
  // links from rendering different rooms. So `roomDeckCount` DRIVES the stage count here
  // rather than merely seeding it; the effect below then reconciles the realm to the new value
  // exactly as a user-driven count change would. Safe to be fully controlled: `roomActive` hides
  // the count switcher, so there is no local input to fight with.
  useEffect(() => {
    if (!roomActive || !Number.isFinite(roomDeckCount)) return;
    const next = clampSubtype(roomDeckCount as number);
    setSubtype((current) => (current === next ? current : next));
  }, [roomActive, roomDeckCount]);

  // Drive the realm's roster when the visible stage count changes (after the new stage DOM is committed,
  // so a grow's added voices find their freshly-rendered stage). Never fires on mount (prev === subtype).
  useEffect(() => {
    const prev = prevSubtypeRef.current;
    if (prev !== subtype) {
      if (activeIndex >= subtype) setActiveIndex(subtype - 1); // keep focus in range on shrink
      onSubtypeChange?.(subtype, prev);
      prevSubtypeRef.current = subtype;
    }
  }, [subtype, activeIndex, onSubtypeChange]);

  // The reserved top strip is not a constant: the room's score bar appears when play starts and collapses
  // again, and every mobile stage is sized against it. If it changes while the pager is parked on a stage
  // other than the first, the stack reflows under a scroll offset that was correct for the OLD strip and
  // the orbiter drifts out from under the bar without anyone navigating. Re-assert the aligned offset for
  // the stage we are on whenever the container's content box changes — the root is pinned to the viewport,
  // so a padding (or dvh / orientation) change is exactly what moves it. A pager tween in flight is left
  // alone: it is already animating towards the fresh geometry.
  useEffect(() => {
    if (!isMobile || typeof ResizeObserver === 'undefined') return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      if (scrollRafRef.current != null) return;
      const block = blockRefs.current.get(activeIndex);
      if (block) container.scrollTop = alignedScrollTop(container, block);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [isMobile, activeIndex]);

  /** Take down whatever divider drag is in flight, without reporting anything. */
  const endDrag = useCallback(() => {
    const drag = dragRef.current;
    if (drag.move) document.removeEventListener('pointermove', drag.move);
    if (drag.up) document.removeEventListener('pointerup', drag.up);
    if (drag.cancel) document.removeEventListener('pointercancel', drag.cancel);
    dragRef.current = {};
    setResizing(false);
  }, []);

  // Remove any lingering resize-drag listeners / in-flight pager scroll-tween if the layout unmounts.
  useEffect(
    () => () => {
      const drag = dragRef.current;
      if (drag.move) document.removeEventListener('pointermove', drag.move);
      if (drag.up) document.removeEventListener('pointerup', drag.up);
      if (drag.cancel) document.removeEventListener('pointercancel', drag.cancel);
      dragRef.current = {};
      if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current);
    },
    [],
  );

  const setRatio = useCallback((ratio: Ratio, value: number) => {
    if (ratio === 'y') setSplitY(value);
    else if (ratio === 'xPrimary') setSplitXPrimary(value);
    else setSplitXSecondary(value);
  }, []);

  const startDrag = (def: { ratio: Ratio; axis: 'x' | 'y' }) => (e: ReactPointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const grid = gridRef.current;
    if (!grid) return;
    // One drag at a time. A second handle pressed before the first is released would otherwise install
    // a second pair of listeners over the top, and either release would then settle BOTH drags — each
    // reporting its own snapshot, the later one overwriting the earlier.
    endDrag();
    const pointerId = e.pointerId;
    const base = def.ratio === 'y' ? splitY : def.ratio === 'xPrimary' ? splitXPrimary : splitXSecondary;
    const start = def.axis === 'x' ? e.clientX : e.clientY;
    // Where the divider actually ended up. Tracked here rather than read back off state on release: a
    // pointerup arrives in the same tick as the last pointermove, so the state behind it may not have
    // re-rendered yet and the saved size would be one move stale.
    let settled = base;
    setResizing(true);
    const onMove = (ev: PointerEvent) => {
      // Only the finger that started this drag moves it. On a touchscreen a second one is somebody
      // resting a hand, or a pinch — not a smaller studio.
      if (ev.pointerId !== pointerId) return;
      const size = def.axis === 'x' ? grid.clientWidth : grid.clientHeight;
      if (!size) return;
      const cur = def.axis === 'x' ? ev.clientX : ev.clientY;
      settled = snapRatio(base + (cur - start) / size);
      setRatio(def.ratio, settled);
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return; // another pointer's release is not this drag's end
      endDrag();
      // Only a divider that MOVED is worth remembering — a click on the handle that went nowhere is
      // not a resize, and writing it would rewrite the arrangement for nothing.
      if (settled !== base) reportGeometry({ [RATIO_FIELD[def.ratio]]: settled });
    };
    // A cancelled pointer (the browser took it for a scroll or a system gesture) ends the drag where it
    // stands and reports NOTHING: the size was never let go of, so it is not a size anybody chose.
    const onCancel = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      endDrag();
    };
    dragRef.current = { move: onMove, up: onUp, cancel: onCancel };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);
  };

  // ── Arrange UX ────────────────────────────────────────────────────────────────────────────────
  // Available to EVERYONE who can view — the handlers are always supplied in collection mode. `canArrange`
  // is just a presence check for a bare (no-arrange) mount. Only Save (persist) is edit-gated.
  const allDrawerEntries = entries ?? [];
  // The section's dealt HAND, if the room is dealing one: the rail shows those cards and no others.
  // Filtered HERE, at render level — the entry list the realm resolves remote deck commands and
  // late-join snapshots through is deliberately left whole. A dealt card the collection couldn't
  // resolve joins the rail as an unavailable placeholder rather than quietly going missing.
  // Memoized on the deal, not the render: the studio re-renders on every drag hint and load-state
  // change, and rebuilding the Set plus a filtered copy of the whole entry list on each of those
  // hands children a fresh array identity for a rail that has not changed since the last deal.
  const hand = useMemo(() => (handSourceIds ? new Set(handSourceIds) : null), [handSourceIds]);
  // Identity of the dealt hand, for the drawer's deal animation. `handSourceIds` is reassigned only
  // on an actual re-deal (section change, dice, pool arrival), so bumping a serial on its identity
  // replays the deal exactly then — never on ordinary re-renders. Null outside a hand → no animation.
  const dealSerialRef = useRef(0);
  const lastHandRef = useRef<typeof handSourceIds>(undefined);
  if (handSourceIds !== lastHandRef.current) {
    lastHandRef.current = handSourceIds;
    dealSerialRef.current += 1;
  }
  const dealId = handSourceIds ? String(dealSerialRef.current) : null;
  // In DEAL order, not drawer order: each player's drawer order is their own (reordered before the
  // room, persisted), so filtering the drawer list would show the same six cards in two different
  // sequences on two screens — and deal the flip animation in two different sequences. The hand's
  // source ids already carry the deal's order (tracks, then orbiters, then worlds).
  //
  // A card that has been PLAYED drops out here and the rest close up — the hand shows what is left
  // to play, and nothing else. The card's own key is `dealId:voiceId`, so the survivors keep their
  // identity and simply reflow; only a real re-deal remounts the rail and replays the animation.
  // Fingerprinted rather than passed by identity because the played set is rebuilt on every studio
  // push (a drag hint, a load finishing), and the rail must not churn on those.
  //
  // ...but NOT while it is still loading. A card is spent the instant the deal is accepted, because
  // every client has to spend it at the same moment or the hands diverge — and that is several
  // seconds before the deck it was played onto can make a sound. Dropping it at spend time meant the
  // card just disappeared and nothing on screen said a deck was coming: the loader this rail already
  // draws had nothing left to draw it on. So a played card stays exactly where it is, showing its
  // loader, and leaves the hand when the orbiter is actually up.
  const consumedKey = handConsumedIds?.join(',') ?? '';
  const loadingKey = loadingSourceIds?.join(',') ?? '';
  // A played card is not dropped the moment it is spent. It stays on the rail to turn face-down,
  // be seen, and slide back out to the right — so the rail keeps rendering it for exactly as long
  // as that takes. A card removed at spend time has no frames left to animate in, which is why it
  // read as blinking out of existence however the exit was drawn.
  const [exitingIds, setExitingIds] = useState<string[]>([]);
  const exitTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const railEntries = useMemo(() => {
    if (!handSourceIds) return allDrawerEntries;
    const byVoiceId = new Map(allDrawerEntries.map((e) => [e.voiceId, e]));
    const played = new Set(consumedKey ? consumedKey.split(',') : []);
    const loading = new Set(loadingKey ? loadingKey.split(',') : []);
    const dealt = handSourceIds
      .filter((voiceId) => !played.has(voiceId) || loading.has(voiceId) || exitingIds.includes(voiceId))
      .map((voiceId) => byVoiceId.get(voiceId))
      .filter((e): e is DrawerEntry => Boolean(e));
    return [...dealt, ...(handMissingEntries ?? [])];
  }, [handSourceIds, allDrawerEntries, handMissingEntries, consumedKey, loadingKey, exitingIds]);

  // Every card of this hand whose exit has already been dealt with — started or long finished.
  // A card is played ONCE, so its exit runs ONCE, and this is what says so. Without it the only
  // guard was "is a timer running", and a timer is gone the moment it fires: every later play
  // re-ran the exit for every card that had already left, so playing a second card brought the
  // first one back onto the rail to leave a second time alongside it.
  //
  // Scoped to the hand, because a re-deal can legitimately deal the same card again and that one
  // must be able to leave on its own account. `dealId` is the existing identity of a dealt hand —
  // it changes only on a real re-deal, which is exactly when this record stops applying.
  const exitedRef = useRef(new Set<string>());
  const exitedDealRef = useRef<string | null>(null);

  // Start a card's exit the moment its deck is up: PLAYED, and no longer loading. Starting it at
  // play time instead would turn the card face-down while its deck was still silent, and the turn
  // is the one thing telling the player the deck is ready.
  useEffect(() => {
    if (!handSourceIds) return;
    const timers = exitTimersRef.current;
    const played = new Set(consumedKey ? consumedKey.split(',') : []);
    const loading = new Set(loadingKey ? loadingKey.split(',') : []);
    if (exitedDealRef.current !== dealId) {
      // A new hand. Nothing from the last one is still leaving, and nothing it remembers applies.
      exitedDealRef.current = dealId;
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
      setExitingIds([]);
      // ...except that a hand can ARRIVE already partly played. A late joiner builds the hand and
      // folds in the room's spent cards before the rail ever draws it, so its first render already
      // has cards that were played — and finished — before this player got here. Those are gone,
      // not leaving: recorded as exited up front, or the next push would find them "played, not
      // loading, never exited" and bring each one onto the rail to animate out long after it left.
      // A card still LOADING is left out of the record, so it exits normally once its deck is up.
      exitedRef.current = new Set([...played].filter((voiceId) => !loading.has(voiceId)));
    }
    for (const voiceId of handSourceIds) {
      if (!played.has(voiceId) || loading.has(voiceId)) continue;
      if (exitedRef.current.has(voiceId)) continue;
      // Marked BEFORE the timer, so a re-render landing inside this same tick cannot start a
      // second exit for the same card.
      exitedRef.current.add(voiceId);
      setExitingIds((prev) => (prev.includes(voiceId) ? prev : [...prev, voiceId]));
      timers.set(
        voiceId,
        setTimeout(() => {
          timers.delete(voiceId);
          setExitingIds((prev) => prev.filter((id) => id !== voiceId));
        }, HAND_CARD_EXIT_MS),
      );
    }
  }, [handSourceIds, dealId, consumedKey, loadingKey]);

  // A rail unmounted mid-exit must not leave timers behind to call setState afterwards.
  useEffect(() => {
    const timers = exitTimersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);
  // Entity filters (mixed collections only): the drawer's left toolbar hides card TYPES from the
  // rail — pure view state, decks and the persisted order are untouched. All types start visible;
  // a homogeneous collection offers no filters (nothing to narrow).
  const [visibleKinds, setVisibleKinds] = useState<Record<EntityKind, boolean>>({
    track: true,
    orbiter: true,
    world: true,
  });
  const presentKinds: EntityKind[] = ENTITY_KINDS.filter((kind) =>
    allDrawerEntries.some((e) => entityKindOf(e.entityType) === kind),
  );
  const mixedCollection = presentKinds.length >= 2;
  const drawerEntries = mixedCollection
    ? railEntries.filter((e) => {
        const kind = entityKindOf(e.entityType);
        return !kind || visibleKinds[kind];
      })
    : railEntries;
  const entityFilters = mixedCollection
    ? presentKinds.map((kind) => ({
        key: kind,
        icon: entityMeta(kind).icon,
        colorVar: entityMeta(kind).colorVar,
        label: entityMeta(kind).label,
        enabled: visibleKinds[kind],
        onToggle: () => setVisibleKinds((prev) => ({ ...prev, [kind]: !prev[kind] })),
      }))
    : null;
  const canArrange = Boolean(onLoadStage);

  // The offline library, bound to THIS collection. Given the unfiltered list on purpose: the entity
  // chips above hide kinds from the rail, which is a view, and downloading the view rather than the
  // collection would quietly leave out whatever happened to be filtered off at the time.
  const offline = useOfflineCollection({
    collectionId,
    collectionName,
    // Stored with the download so a collection played offline is arranged the way it was left,
    // rather than rebuilt at the default stage layout.
    collectionLayout: savedLayout,
    // A file on this machine is not something a download could store, or should try to.
    entries: allDrawerEntries.filter((entry) => !entry.local),
  });
  // Desktop only, matching the feature's own scope: a phone is where the buffered-playback
  // memory ceiling bites hardest, and offering a download there would promise a playback path that
  // has not been measured. Never inside a room either — a room grant is a READ grant over someone
  // else's card pool, and keeping a copy of it is not what it permits.
  const offlineAllowed = offline.enabled && !isMobile && !roomActive;
  const offlineControl = offlineAllowed ? (
    <OfflineDownloadControl offline={offline} canDownload={Boolean(collectionId)} />
  ) : null;
  // Root's "Download for offline" arrives as `?offline=download`. Honoured under exactly the gate
  // the control has, so a phone or a room never starts a download it would not offer a button for.
  useOfflineDownloadIntent({
    offline,
    ready: Boolean(collectionId) && allDrawerEntries.length > 0 && offline.loaded,
    allowed: offlineAllowed,
  });

  // Show the drawer wherever arrange is available and the COLLECTION has cards — gated on the
  // unfiltered list, deliberately: filtering every kind out must leave the drawer (and its filter
  // chips) on screen with an empty rail, or there would be no way to turn the kinds back on. It
  // stays mounted when closed (collapsed to 0 height) so its resized height survives a
  // Cards-toggle. Desktop mounts it IN-FLOW (its height reflows the grid); mobile mounts it as a
  // FIXED overlay over the stage pager.
  const canShowDrawer = canArrange && (allDrawerEntries.length > 0 || Boolean(localRail));
  const drawerMounted = !isMobile && canShowDrawer;
  // Reserve the drawer's strip only when it is actually on screen (mounted AND open) — otherwise a bare
  // mount, whose drawerOpen stays true with no Cards toggle to flip it, would strand dead space.
  const mobileDrawerShown = canShowDrawer && drawerOpen;

  // The box a mobile stage's content (the orbiter canvas OR the empty placeholder) may occupy: the visible
  // area MINUS the nav bar and — when the drawer is shown — the reduced drawer, so the orbiter shrinks to
  // make room (reflow, like desktop) and the placeholder never hides under the bar. Portrait: reserve the
  // bottom strip; landscape: reserve the left rail, and the drawer's height along the bottom. The
  // ViewportCompositor re-reads the stage rect each frame, so shrinking this box re-frames the voice.
  const mobileContentInset: CSSProperties = isLandscape
    ? { position: 'absolute', top: 0, right: 0, left: MOBILE_RAIL_W, bottom: mobileDrawerShown ? MOBILE_DRAWER_H : 0 }
    : {
        position: 'absolute',
        top: 0,
        right: 0,
        left: 0,
        bottom: mobileDrawerShown ? `calc(${MOBILE_BAR_INSET} + ${MOBILE_DRAWER_H}px)` : MOBILE_BAR_INSET,
      };

  // A lightweight pointer drag session (no dnd-kit): a card press past the activation distance starts a
  // drag; while dragging we hit-test the pointer against stage boxes (drop → load) and drawer cards
  // (drop → reorder). Refs hold the hit-test targets; a little state drives the overlay + drop hints.
  const cardRefs = useRef<Map<string, HTMLElement>>(new Map());
  const endRef = useRef<HTMLElement | null>(null);
  // The drawer's horizontal scroll container (registered by CardDrawer) — we scroll it ourselves during a
  // 'scroll' gesture, since the cards are `touch-action: none` and no longer scroll the rail natively.
  const railScrollRef = useRef<HTMLElement | null>(null);
  // In-flight carousel fling (rAF id), so a re-grab cancels the coast and unmount stops it.
  const scrollMomentumRef = useRef<number | null>(null);
  // Last card tap (voice + timestamp) for double-tap-to-load detection across two separate pointer sessions.
  const lastCardTapRef = useRef<{ voiceId: string; t: number } | null>(null);
  const cardDragRef = useRef<{
    entry: DrawerEntry;
    startX: number;
    startY: number;
    // 'pending' until the first move locks an axis, then 'scroll' (own the carousel) or 'drag' (lift the card).
    mode: 'pending' | 'scroll' | 'drag';
    isTouch: boolean;
    startScrollLeft: number; // the rail's scrollLeft when a 'scroll' gesture began (finger-follow origin)
    lastScrollX: number; // last pointer X during a scroll (for the release-velocity estimate)
    lastScrollT: number; // timestamp of that sample
    scrollVel: number; // scrollLeft velocity (px/ms) at the latest sample — seeds the fling
    move: (e: PointerEvent) => void;
    up: (e: PointerEvent) => void;
    /** Abandon the session (pointercancel, or a context menu taking over the press). */
    cancel: () => void;
  } | null>(null);
  const [dragEntry, setDragEntry] = useState<DrawerEntry | null>(null);
  // The overlay position is driven IMPERATIVELY (a ref + direct style write) so a pointermove doesn't
  // re-render the whole studio each pixel — only the drop-hint state below changes, and only when the
  // pointer crosses a stage/card boundary (React bails out on an unchanged value).
  const dragPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [dropStage, setDropStage] = useState<number | null>(null);
  const [dropBeforeVoiceId, setDropBeforeVoiceId] = useState<string | null>(null);
  const [dropAtEnd, setDropAtEnd] = useState(false);
  // The drop target on release is read from refs (the pointerup closure would otherwise capture stale
  // state). Kept in sync with the drop-hint state every render, below.
  const dropBeforeRef = useRef<string | null>(null);
  const dropAtEndRef = useRef(false);
  dropBeforeRef.current = dropBeforeVoiceId;
  dropAtEndRef.current = dropAtEnd;

  const registerCardRef = useCallback((voiceId: string, node: HTMLElement | null) => {
    if (node) cardRefs.current.set(voiceId, node);
    else cardRefs.current.delete(voiceId);
  }, []);

  const clearDropHints = () => {
    setDropStage(null);
    setDropBeforeVoiceId(null);
    setDropAtEnd(false);
  };

  const pointInRect = (box: DOMRect | undefined, x: number, y: number): boolean =>
    !!box && x >= box.left && x < box.right && y >= box.top && y < box.bottom;

  // Which stage box (if any) contains the pointer — only front stages that can actually hold a voice.
  const stageIndexAtPoint = (x: number, y: number): number | null => {
    for (let i = 0; i < subtype; i++) {
      if (pointInRect(stageRefs.current.get(i)?.getBoundingClientRect(), x, y)) return i;
    }
    return null;
  };

  /**
   * Where a drag out of the local-files panel is hovering, and what a release there would do.
   *
   * The panel's list owns the GESTURE — the axis lock, the pointer capture, the preview — and hands
   * over coordinates. Everything positional is here, because only the studio knows what a stage or
   * a rail is, and the design library is deliberately never told.
   *
   * The stage highlight reuses `fileDropStage`, the one an OS file drag already paints. Two ways in,
   * one piece of feedback: a second highlight that looked slightly different would be a second
   * mechanism to keep in step.
   */
  const localPanelRef = useRef<HTMLElement | null>(null);

  const localDragTargetAt = (x: number, y: number): { kind: 'stage'; index: number } | { kind: 'rail' } | null => {
    // Nothing under the panel itself is a target. On a phone the panel is a sheet laid OVER the
    // stages, so a release inside it is geometrically inside a stage too — and would load a file
    // onto a deck the person cannot even see. Releasing over the list you dragged from means
    // "never mind", which is also the honest reading on a desktop.
    if (pointInRect(localPanelRef.current?.getBoundingClientRect(), x, y)) return null;
    const stage = stageIndexAtPoint(x, y);
    if (stage !== null) return { kind: 'stage', index: stage };
    // The rail's own scroller — anywhere along the cards means "put it in this collection".
    if (pointInRect(railScrollRef.current?.getBoundingClientRect(), x, y)) return { kind: 'rail' };
    return null;
  };

  const [localDragOverRail, setLocalDragOverRail] = useState(false);

  const clearLocalDragTargets = useCallback(() => {
    setFileDropStage(null);
    setLocalDragOverRail(false);
  }, []);

  const onLocalRowsDragMove = (_ids: string[], x: number, y: number) => {
    const target = localDragTargetAt(x, y);
    setFileDropStage(target?.kind === 'stage' ? target.index : null);
    setLocalDragOverRail(target?.kind === 'rail');
  };

  const onLocalRowsDrop = (ids: string[], x: number, y: number) => {
    const target = localDragTargetAt(x, y);
    clearLocalDragTargets();
    if (!target || !localRail) return;
    // A release over nothing is not a mistake to report — it is the ordinary way to change your
    // mind mid-drag, and saying something about it would punish that.
    if (target.kind === 'stage') void localRail.onStageEntries(ids, target.index);
    else void localRail.onAddToCollection(ids);
  };

  const onCardPointerDown = (entry: DrawerEntry, e: ReactPointerEvent) => {
    // A press ALWAYS stops any in-flight fling — grabbing the carousel arrests the coast (like catching a
    // spinning record). Hoisted above the guards so it fires even when a press wouldn't start a new session.
    if (scrollMomentumRef.current != null) {
      cancelAnimationFrame(scrollMomentumRef.current);
      scrollMomentumRef.current = null;
    }
    if (!canArrange || cardDragRef.current) return; // one drag at a time (ignore a second concurrent pointer)
    // Only the PRIMARY button grabs a card. A right-click opens the card's context menu, and its
    // pointerup would otherwise land here as a "tap" and half-arm the double-tap-to-load pair.
    if (e.button !== 0) return;
    const isTouch = e.pointerType === 'touch';

    const teardown = () => {
      const session = cardDragRef.current;
      if (!session) return;
      document.removeEventListener('pointermove', session.move);
      document.removeEventListener('pointerup', session.up);
      document.removeEventListener('pointercancel', session.cancel);
      cardDragRef.current = null;
    };

    // Position the overlay + resolve the drop hint for a pointer location (called from pointermove once a
    // drag is armed).
    const applyDragMove = (x: number, y: number) => {
      dragPosRef.current = { x, y };
      if (overlayRef.current) {
        overlayRef.current.style.left = `${x + 12}px`;
        overlayRef.current.style.top = `${y + 12}px`;
      }
      const stage = stageIndexAtPoint(x, y);
      if (stage != null) {
        // Only a valid target shows the drop ring — a card that can't seed an empty stage (a world)
        // gets no highlight over one, so the affordance never promises a dead drop.
        setDropStage(isValidDropTarget(entry, stage) ? stage : null);
        setDropBeforeVoiceId(null);
        setDropAtEnd(false);
        return;
      }
      setDropStage(null);
      // A dealt hand has no user order to set — the next section replaces it — so the rail offers no
      // reorder targets while one is on screen.
      if (hand) {
        setDropBeforeVoiceId(null);
        setDropAtEnd(false);
        return;
      }
      // Not over a stage → resolve a drawer reorder target (a card to insert before, or the end zone).
      let before: string | null = null;
      for (const [voiceId, node] of cardRefs.current) {
        if (voiceId === entry.voiceId) continue;
        if (pointInRect(node.getBoundingClientRect(), x, y)) {
          before = voiceId;
          break;
        }
      }
      const overEnd = !before && pointInRect(endRef.current?.getBoundingClientRect(), x, y);
      setDropBeforeVoiceId(before);
      setDropAtEnd(overEnd);
    };

    const onMove = (ev: PointerEvent) => {
      const session = cardDragRef.current;
      if (!session) return;
      const dx = ev.clientX - session.startX;
      const dy = ev.clientY - session.startY;

      // First real movement locks the axis: a touch that moves mostly sideways scrolls the carousel; a
      // vertical touch (or any mouse / pen move) lifts the card out. Below the lock distance we can't yet
      // tell a scroll from a grab, so wait. EVERY card lifts — even one that can't seed an empty stage
      // (a world): dropped on an occupied stage it swaps its own dimension of that live session.
      if (session.mode === 'pending') {
        if (Math.hypot(dx, dy) < DRAG_AXIS_LOCK_DISTANCE) return;
        if (session.isTouch && Math.abs(dx) > Math.abs(dy)) {
          session.mode = 'scroll';
          session.startScrollLeft = railScrollRef.current?.scrollLeft ?? 0;
          session.lastScrollX = ev.clientX;
          session.lastScrollT = ev.timeStamp;
        } else {
          session.mode = 'drag';
          setDragEntry(session.entry); // mounts the overlay; positioned from dragPosRef on first render
        }
      }

      if (session.mode === 'scroll') {
        // We own the carousel scroll (cards are touch-action:none) — follow the finger 1:1 from the origin.
        const el = railScrollRef.current;
        if (el) el.scrollLeft = session.startScrollLeft - dx;
        // Track the scrollLeft velocity (opposite the finger) so a release can coast (see onUp). Smooth it
        // with a light EMA so a single 1px jitter on the final sample can't flip the fling's direction.
        const dt = ev.timeStamp - session.lastScrollT;
        if (dt > 0) {
          const inst = -(ev.clientX - session.lastScrollX) / dt;
          session.scrollVel = FLING_VEL_SMOOTHING * inst + (1 - FLING_VEL_SMOOTHING) * session.scrollVel;
          session.lastScrollX = ev.clientX;
          session.lastScrollT = ev.timeStamp;
        }
        return;
      }

      ev.preventDefault(); // dragging: suppress text selection / any residual native gesture
      applyDragMove(ev.clientX, ev.clientY);
    };
    const onUp = (ev: PointerEvent) => {
      const session = cardDragRef.current;
      teardown();
      setDragEntry(null);
      if (session?.mode === 'drag') {
        lastCardTapRef.current = null; // a drag is not a tap — don't let it pair with a later tap
        const stage = stageIndexAtPoint(ev.clientX, ev.clientY);
        if (stage != null) {
          // Same validity rule as the drop hint — a release over an invalid stage does nothing (and
          // can't fall through to a reorder: the hints were cleared while hovering it).
          if (isValidDropTarget(session.entry, stage)) onLoadStage?.(stage, session.entry);
          // Reordering is off while a hand is dealt: the rail's order is the deal's, and the next
          // section replaces it. The hints are already cleared above — this also covers a hand that
          // arrives mid-drag, whose section boundary would otherwise land on a stale hint.
        } else if (!hand && dropAtEndRef.current) onReorderEntries?.(session.entry.voiceId, null);
        else if (!hand && dropBeforeRef.current) onReorderEntries?.(session.entry.voiceId, dropBeforeRef.current);
      } else if (session?.mode === 'pending') {
        // A tap (neither a drag nor a carousel scroll). A SECOND tap on the same card within the window
        // loads it into the active orbiter — the device-independent fallback for the critical load action.
        const now = ev.timeStamp;
        const prev = lastCardTapRef.current;
        if (prev && prev.voiceId === session.entry.voiceId && now - prev.t <= DOUBLE_TAP_MS) {
          lastCardTapRef.current = null;
          // Same rule as a drop on the active stage — so a double-tapped world card swaps the active
          // stage's world when one is playing there, and does nothing over an empty stage.
          if (isValidDropTarget(session.entry, activeIndex)) onLoadStage?.(activeIndex, session.entry);
        } else {
          lastCardTapRef.current = { voiceId: session.entry.voiceId, t: now };
        }
      } else {
        // A carousel scroll (or no gesture) is not a tap — don't let it bridge two taps into a false load.
        lastCardTapRef.current = null;
        // Fling: if the release carried enough speed, coast the rail with decaying velocity so the swipe
        // feels physical instead of stopping dead under the finger. A stale last sample means the finger had
        // paused before lifting → no coast (native momentum wouldn't fling a held-then-released swipe).
        const el = railScrollRef.current;
        const sampleFresh = session != null && ev.timeStamp - session.lastScrollT <= FLING_SAMPLE_MAX_AGE;
        let v = session?.mode === 'scroll' && sampleFresh ? session.scrollVel : 0;
        if (el && Math.abs(v) >= FLING_MIN_VELOCITY) {
          v = Math.max(-FLING_MAX_VELOCITY, Math.min(FLING_MAX_VELOCITY, v));
          let prevTs: number | null = null;
          const step = (ts: number) => {
            if (prevTs == null) {
              prevTs = ts;
              scrollMomentumRef.current = requestAnimationFrame(step);
              return;
            }
            const frame = ts - prevTs;
            prevTs = ts;
            const intended = el.scrollLeft + v * frame;
            el.scrollLeft = intended;
            const clampedAtEdge = Math.abs(el.scrollLeft - intended) > 1; // browser clamped → at an edge
            v *= FLING_FRICTION ** (frame / 16);
            if (clampedAtEdge || Math.abs(v) <= FLING_STOP_VELOCITY) {
              scrollMomentumRef.current = null;
              return;
            }
            scrollMomentumRef.current = requestAnimationFrame(step);
          };
          scrollMomentumRef.current = requestAnimationFrame(step);
        }
      }
      clearDropHints();
    };
    // A palm rejection / OS edge gesture can still fire pointercancel instead of pointerup. Without this the
    // session and its document listeners leak. (Cards are touch-action:none, so a rail scroll no longer
    // cancels a live drag — the bug that used to drop a card before it reached a slot.)
    const onCancel = () => {
      teardown();
      setDragEntry(null);
      clearDropHints();
      // An abandoned press is not a tap — leaving one armed would let it pair with a later tap into a
      // load the user never asked for. True for a palm rejection as much as for a menu taking the press.
      lastCardTapRef.current = null;
    };
    cardDragRef.current = {
      entry,
      startX: e.clientX,
      startY: e.clientY,
      mode: 'pending',
      isTouch,
      startScrollLeft: 0,
      lastScrollX: e.clientX,
      lastScrollT: e.timeStamp,
      scrollVel: 0,
      move: onMove,
      up: onUp,
      cancel: onCancel,
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);
  };

  // A card's context menu opened on the same press that armed a drag — a long-press on touch opens it
  // with no pointercancel, so drop the session here or it would still be live behind the menu and drop
  // the card on release.
  const cancelCardDrag = useCallback(() => cardDragRef.current?.cancel(), []);

  // Tear down a card-drag session (and any in-flight fling) if the layout unmounts mid-gesture.
  useEffect(
    () => () => {
      const session = cardDragRef.current;
      if (session) {
        document.removeEventListener('pointermove', session.move);
        document.removeEventListener('pointerup', session.up);
        document.removeEventListener('pointercancel', session.cancel);
        cardDragRef.current = null;
      }
      if (scrollMomentumRef.current != null) {
        cancelAnimationFrame(scrollMomentumRef.current);
        scrollMomentumRef.current = null;
      }
    },
    [],
  );

  // ── Count switcher + Go back (with confirmation before a shrink drops loaded orbiters) ──────────
  const isStageFilled = (index: number) =>
    stageVoiceIds ? Boolean(stageVoiceIds[index]) : index < Math.min(subtype, rosterLength);

  // Where a card may land: an OCCUPIED stage accepts any card (the drop swaps only the card's own
  // dimension of the live session); an EMPTY stage only accepts a card that can boot it by itself.
  // An unavailable card stands for an entity the collection could not resolve — it is on the rail so
  // the hand reads honestly, and it can never be dealt onto a stage.
  const isValidDropTarget = (entry: DrawerEntry, index: number) =>
    entry.unavailable !== true && (isStageFilled(index) || entry.bootable !== false);

  // Derived from (filled?, owning orbiter) per slot — NOT the `stageVoiceIds`/
  // `focusMidiPersistenceIds` array references, which are rebuilt on every collection edit
  // (reorder, an unrelated drag) even when occupancy is unchanged. Using this string as the
  // registration effect's dependency means register/unregister only fires when a slot's fill
  // state or owning orbiter actually changes — not on every unrelated state push.
  const focusRegistrationSignature = Array.from({ length: subtype }, (_, index) =>
    isStageFilled(index) ? `${index}:${focusMidiPersistenceIds?.[index] ?? ''}` : '',
  ).join('|');

  useEffect(() => {
    if (!registerMidiTarget || !unregisterMidiTarget) return undefined;
    const registered: string[] = [];
    for (let index = 0; index < subtype; index++) {
      if (!isStageFilled(index)) continue;
      const element = focusArtworkRefs.current.get(index);
      if (!element) continue;
      // The mapping is "select THIS TRACK" — scoped to that track's own real
      // orbiter under ONE fixed componentId, so it's the SAME learned mapping regardless of which
      // slot the track ends up in (re-registers with the fresh index whenever occupancy changes —
      // see `focusRegistrationSignature` — which is what makes the trigger follow the track rather
      // than a frozen position). `id` only needs to be unique per mounted DOM node this render; it
      // is never the persisted key.
      const persistenceOrbiterId = focusMidiPersistenceIds?.[index] ?? null;
      if (!persistenceOrbiterId) continue;
      const componentId = 'collection-focus-track';
      const id = `pm-collection-focus-track-${index}`;
      registerMidiTarget({
        id,
        element,
        componentId,
        componentType: 'kick',
        scope: 'GLOBAL',
        persistenceScope: { scope: 'orbiter', entityId: persistenceOrbiterId },
        onTrigger: () => focusStage(index, false, true),
      });
      registered.push(id);
    }
    return () => {
      registered.forEach((id) => unregisterMidiTarget(id));
    };
  }, [focusRegistrationSignature, focusStage, registerMidiTarget, subtype, unregisterMidiTarget]);

  // The filled stages in order — drives `hasActivePlayers` (the leave/shrink confirm). The mobile pager
  // itself steps activeIndex ± 1 through ALL stages so an empty stage is reachable to drop a card onto.
  const filledIndices: number[] = [];
  for (let i = 0; i < subtype; i++) if (isStageFilled(i)) filledIndices.push(i);
  const hasActivePlayers = filledIndices.length > 0;

  // The mobile pager steps through ALL stages relative to the focused one (not just filled) so an EMPTY
  // stage is reachable — that's how you page to a fresh stage to drop a card onto it after growing the
  // layout. `null` at the ends disables the arrow.
  const pagerPrevIndex = activeIndex > 0 ? activeIndex - 1 : null;
  const pagerNextIndex = activeIndex < subtype - 1 ? activeIndex + 1 : null;

  // ── Shell MIDI targets (collection scope) ────────────────────────────────────────────────────────
  // The studio's OWN actions — slot focus/add by POSITION (regardless of occupant), pager, drawer —
  // persisted under the collection, so a controller layout is learned once per collection and works
  // whatever is loaded. Pager + Cards targets are the REAL dock buttons (standard learn overlays,
  // tinted by scope); only the per-slot focus/add actions, which have no existing control, use the
  // learn-only chips rendered on each stage (zero-size outside `body.midi-learn-mode`). Trigger
  // callbacks read live state through a ref so registration never re-fires on focus/drawer churn.
  const shellAnchorRefs = useRef<Map<string, HTMLElement>>(new Map());
  const getShellAnchorRefCb = useCallback(
    (key: string) => (el: HTMLElement | null) => {
      if (el) shellAnchorRefs.current.set(key, el);
      else shellAnchorRefs.current.delete(key);
      return undefined;
    },
    [],
  );
  const shellActionsRef = useRef({
    focus: (_i: number) => {},
    next: () => {},
    prev: () => {},
    drawer: () => {},
  });
  shellActionsRef.current = {
    focus: (i: number) => focusStage(i, false, true),
    next: () => navigateToStage(pagerNextIndex),
    prev: () => navigateToStage(pagerPrevIndex),
    drawer: () => setDrawerShown(!drawerOpen),
  };

  useEffect(() => {
    if (!shellMidiCollectionId || !registerMidiTarget || !unregisterMidiTarget) return undefined;
    const persistenceScope = { scope: 'collection', entityId: shellMidiCollectionId };
    const registered: string[] = [];
    const register = (componentId: string, onTrigger: () => void) => {
      const element = shellAnchorRefs.current.get(componentId);
      if (!element) return;
      const id = `pm-${componentId}`;
      registerMidiTarget({
        id,
        element,
        componentId,
        componentType: 'kick',
        scope: 'GLOBAL',
        persistenceScope,
        onTrigger,
      });
      registered.push(id);
    };
    // One select target per POSITION. Additive multi-select from MIDI was dropped on purpose
    // (8 chips read as clutter); if it ever returns it should be a shift-style toggle, not a
    // second chip per slot.
    for (let index = 0; index < subtype; index++) {
      register(`collection-stage-focus-${index + 1}`, () => shellActionsRef.current.focus(index));
    }
    register('collection-stage-prev', () => shellActionsRef.current.prev());
    register('collection-stage-next', () => shellActionsRef.current.next());
    register('collection-drawer-toggle', () => shellActionsRef.current.drawer());
    return () => {
      registered.forEach((id) => unregisterMidiTarget(id));
    };
    // isMobile + canShowDrawer/canArrange swap WHICH dock buttons are mounted — re-register so
    // the targets rebind to the freshly mounted elements.
  }, [shellMidiCollectionId, subtype, registerMidiTarget, unregisterMidiTarget, isMobile, canShowDrawer, canArrange]);

  const requestSubtype = (next: number) => {
    const clamped = clampSubtype(next);
    if (clamped === subtype) return;
    // A shrink drops the loaded voices on the stages beyond the new count (index-based, no relocation —
    // survivors keep their audio). Confirm before dropping any; a grow / same commits directly.
    if (clamped < subtype) {
      let dropped = 0;
      for (let i = clamped; i < subtype; i++) if (isStageFilled(i)) dropped += 1;
      if (dropped > 0) {
        setPendingConfirmation({ kind: 'shrink', next: clamped, dropped });
        return;
      }
    }
    setSubtype(clamped);
  };

  const requestBack = () => {
    if (hasActivePlayers) setPendingConfirmation({ kind: 'back' });
    else onBack?.();
  };

  const confirmProceed = () => {
    const pending = pendingConfirmation;
    setPendingConfirmation(null);
    if (!pending) return;
    if (pending.kind === 'back') onBack?.();
    else setSubtype(pending.next);
  };

  const stageStyles = slotStylesFor(subtype, splitY, splitXPrimary, splitXSecondary);
  const dividers = dividersFor(subtype, splitY, splitXPrimary, splitXSecondary);

  // Shared dock controls used by BOTH the desktop and mobile bars (same model everywhere): the
  // active-orbiter MONITOR (which opens the orbiter map) and the right-aligned Options menu. Rendered
  // inline (plain values, not nested components) so they don't remount.

  // The active-orbiter monitor. It shows the active slot's LETTER always, and — when that slot has a
  // track loaded — its album ARTWORK, so the loaded track is identifiable at a glance (the per-stage
  // artwork overlay was removed from the play area for space).
  const activeEntry = stageEntries?.[activeIndex];
  const activeFilled = isStageFilled(activeIndex);
  const activeLetter = String(SLOT_LABELS[activeIndex] ?? activeIndex + 1);
  // The monitor is also the ORBITER MAP's trigger: a click opens a drop-up that mirrors the live stage
  // layout, so "which card is playing in which position" is answerable without leaving the view. Click
  // (not hover) on purpose — the same gesture has to work under a finger.
  const MAP_W = isMobile ? 244 : 304;
  const MAP_H = isMobile ? 208 : 248;
  // The cover scales with how much room a tile gets in the current layout, so a 1-up map shows a big
  // preview and a 2×2 still fits its four above their two lines of label.
  const mapCoverSize = subtype <= 1 ? 104 : subtype === 2 ? 88 : 54;

  // Multi-select inside the map, matching the grid's own gesture: shift-click a position to add or
  // remove it, and the map STAYS OPEN so a set can be built in one visit. Touch has no shift key, so a
  // drag across positions paints the same additive selection — which is also the faster gesture with a
  // mouse. Both funnel into the one `additive` path the grid already uses (`onFocusStage(i, true)` →
  // `voiceRegistry.toggleSelection`); the map only decides WHICH positions to send.
  const isPositionSelected = (index: number) =>
    selectedIndices.length > 0 ? selectedIndices.includes(index) : index === activeIndex;
  // A paint session: the tiles this drag has already sent (never send one twice — `toggleSelection`
  // would undo it), and whether it moved at all, which is what separates a paint from a plain tap.
  const mapPaintRef = useRef<{ pointerId: number | null; sent: Set<number>; painted: boolean }>({
    pointerId: null,
    sent: new Set(),
    painted: false,
  });
  // Close the session — the pointer is gone, whatever route it left by. `painted` deliberately survives:
  // the click it suppresses has not fired yet (the capture handler below clears it).
  const endMapPaint = () => {
    mapPaintRef.current.pointerId = null;
  };
  const paintPosition = (index: number) => {
    const session = mapPaintRef.current;
    if (session.sent.has(index)) return;
    session.sent.add(index);
    // Only ADD: a paint that toggled would deselect whatever the finger happened to cross.
    if (!isPositionSelected(index)) focusStage(index, true);
  };
  // The pointer is captured by the tile it went down on (implicit on touch), so the tile a drag has
  // moved ONTO can't be read from the event target — hit-test the point instead.
  const positionUnderPointer = (clientX: number, clientY: number): number | null => {
    const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    const tile = el?.closest?.('[data-orbiter-map-slot]') as HTMLElement | null;
    const raw = tile?.dataset?.orbiterMapSlot;
    return raw == null ? null : Number(raw);
  };

  const trackMonitor = (
    <Popover open={mapOpen} onOpenChange={setMapOpen}>
      <PopoverTrigger asChild>
        <CornerButton
          kind="kick"
          icon={
            activeFilled ? (
              <StationTrackCover
                image={activeEntry?.image}
                title={activeEntry?.title || 'Untitled'}
                size={20}
                radius={5}
                fallbackFontSize="0.6rem"
                entityType={activeEntry?.entityType}
              />
            ) : (
              <OrbiterGlyph />
            )
          }
          label={activeLetter}
          dropIndicator="up"
          data-orbiter-map="trigger"
          aria-label={
            activeFilled
              ? t('studio.dock.mapLoaded', {
                  letter: activeLetter,
                  title: activeEntry?.title || t('studio.dock.monitorLoadedFallback'),
                })
              : t('studio.dock.mapEmpty', { letter: activeLetter })
          }
        />
      </PopoverTrigger>
      {/* Same portal treatment as the Options menu: portals to <body> (outside the studio's `dark` scope),
          so re-scope it dark and let `.orb-studio__portal-surface` map the USER chrome tokens. */}
      <PopoverContent
        side="top"
        align="center"
        className="dark orb-studio__portal-surface"
        style={{ width: 'auto', padding: 10 }}
      >
        {/* The map IS the layout: the same `slotStylesFor` rects the stages use, at map scale — including
            the user's live split ratios — so a tile sits exactly where its orbiter sits on screen. */}
        {/* Never taller than the room the drop-up actually has above the bar: the tiles are positioned in
            PERCENTAGES, so shrinking this box scales the whole map instead of clipping its top row (Radix
            hands us the free space in `--radix-popover-content-available-height`). */}
        <div
          onPointerDown={(event) => {
            // Left button / first finger only. A right-click, or a second finger landing mid-drag, must
            // not seize the session: it would reset `painted` under the finger that is still painting,
            // and that finger's lift would then navigate away on top of the selection it just built.
            if (event.button !== 0 || !event.isPrimary) return;
            const session = mapPaintRef.current;
            if (session.pointerId !== null) return;
            session.pointerId = event.pointerId;
            session.sent.clear();
            session.painted = false;
          }}
          onPointerMove={(event) => {
            const session = mapPaintRef.current;
            if (session.pointerId !== event.pointerId) return;
            // The press may have ended somewhere this container never hears about — the popover's own
            // padding ring, or off-screen entirely. Without this the session would stay open and every
            // later HOVER would paint, silently rescoping the realm's selection with no click at all.
            if (event.buttons === 0) {
              endMapPaint();
              return;
            }
            const index = positionUnderPointer(event.clientX, event.clientY);
            if (index == null) return;
            // The first position only counts once the drag has actually left it for another — otherwise
            // a plain tap (which always reports a move or two) would paint instead of navigating.
            if (!session.painted && session.sent.size === 0) {
              session.sent.add(index);
              return;
            }
            if (session.sent.has(index)) return;
            if (!session.painted) {
              // It IS a drag: commit the position it started on, then this one.
              session.painted = true;
              const [start] = [...session.sent];
              session.sent.clear();
              if (start != null) paintPosition(start);
            }
            paintPosition(index);
          }}
          onPointerUp={endMapPaint}
          onPointerCancel={endMapPaint}
          onLostPointerCapture={endMapPaint}
          // The click after a paint lands on whatever ancestor the drag's endpoints share — usually THIS
          // container, not either tile — so the suppression has to sit here, in capture, where it runs
          // before the tile's own handler and always clears the flag. Clearing it on pointerup instead
          // would be too early: the click has not happened yet.
          onClickCapture={(event) => {
            if (!mapPaintRef.current.painted) return;
            mapPaintRef.current.painted = false;
            event.stopPropagation();
            event.preventDefault();
          }}
          style={{
            position: 'relative',
            width: MAP_W,
            height: `min(${MAP_H}px, calc(var(--radix-popover-content-available-height, 100dvh) - 28px))`,
            // A drag across the map is a selection gesture, so the browser must not claim it for
            // scrolling first — without this the paint never starts under a finger.
            touchAction: 'none',
          }}
        >
          {stageStyles.map((rect, index) => {
            const entry = stageEntries?.[index];
            const filled = isStageFilled(index);
            const letter = String(SLOT_LABELS[index] ?? index + 1);
            const isActive = index === activeIndex;
            const isSelected = isPositionSelected(index);
            const goLabel = filled
              ? t('studio.dock.mapGoLoaded', {
                  letter,
                  title: entry?.title || t('studio.dock.monitorLoadedFallback'),
                })
              : t('studio.dock.mapGoEmpty', { letter });
            return (
              <button
                key={index}
                type="button"
                data-orbiter-map-slot={index}
                data-orbiter-map-selected={isSelected ? 'true' : undefined}
                onClick={(event) => {
                  if (event.shiftKey) {
                    focusStage(index, true);
                    return; // building a set: the map stays open
                  }
                  setMapOpen(false);
                  navigateToStage(index);
                }}
                // A plain click NAVIGATES (and closes the map), so this is not a toggle button and must
                // not be announced as one — `aria-pressed` would misdescribe what activating it does.
                // The selection is state ABOUT the position, so it rides in the name instead.
                aria-label={isSelected ? `${goLabel} — ${t('studio.dock.mapSelected')}` : goLabel}
                aria-current={isActive ? 'true' : undefined}
                style={{
                  position: 'absolute',
                  ...rect,
                  padding: 4,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--color-foreground, #ffffff)',
                  cursor: 'pointer',
                }}
              >
                {/* Corner brackets, not a boxed border — the same frame the collection's stages and cards
                    already wear, so a map tile reads as the same object as the position it stands for. */}
                {/* Same language as the grid's own focus marker (MultiOrbiterFocusFrame): the primary
                    draws solid corners, co-selected positions draw dimmer ones. Unselected keeps a faint
                    frame — in the map the corners are also the card, so they can't vanish entirely — and
                    the selected corners are LONGER as well as brighter, which is what stays legible at
                    map scale where a brightness step alone does not. */}
                <FourCornerCard
                  cornerColors={
                    isActive
                      ? 'var(--color-foreground, #ffffff)'
                      : isSelected
                        ? 'color-mix(in oklab, var(--color-foreground, #ffffff) 55%, transparent)'
                        : 'color-mix(in oklab, var(--color-foreground, #ffffff) 25%, transparent)'
                  }
                  cornerWidth={isSelected ? 18 : 11}
                  cornerHeight={isSelected ? 16 : 9}
                  // The whole tile is one click target and the map is read at a glance, so every frame
                  // stays drawn rather than waiting for a hover that never comes on touch.
                  alwaysShowCorners
                  style={{ width: '100%', height: '100%' }}
                  contentClassName="flex h-full flex-col items-center justify-center"
                >
                  {filled ? (
                    <StationTrackCover
                      image={entry?.image}
                      title={entry?.title || 'Untitled'}
                      size={mapCoverSize}
                      radius={10}
                      fallbackFontSize="1.25rem"
                      entityType={entry?.entityType}
                    />
                  ) : (
                    <span style={{ opacity: 0.45, display: 'inline-flex' }}>
                      <OrbiterGlyph />
                    </span>
                  )}
                  {/* Letter over title, both centred: the position is what you scan for, the track is what
                      confirms it. */}
                  <span style={{ marginTop: 6, fontSize: '0.8rem', lineHeight: 1.1, fontWeight: 600, textAlign: 'center' }}>
                    {letter}
                  </span>
                  <span
                    style={{
                      maxWidth: '100%',
                      fontSize: '0.7rem',
                      lineHeight: 1.2,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: filled
                        ? 'var(--color-foreground, #ffffff)'
                        : 'var(--color-muted-foreground, rgba(255,255,255,0.62))',
                    }}
                  >
                    {filled ? entry?.title || t('studio.dock.monitorLoadedFallback') : t('studio.dock.mapSlotEmpty')}
                  </span>
                </FourCornerCard>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
  // Deferred past the menu close before opening a dialog — two Radix modals in one tick can wedge the
  // body pointer-events lock (the same reason the count change defers; leaving may open its confirm).
  const openStartRoom = () =>
    requestAnimationFrame(() => {
      if (stagedLocalCount > 0) {
        notifications.showToast(t('studio.local.roomRefused'), 'warning', 6000);
        return;
      }
      setStartRoomOpen(true);
    });
  const sessionActions = (
    <>
      {canStartRoom ? (
        <>
          <DropdownMenuItem onSelect={openStartRoom}>{t('studio.dock.startRoom')}</DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      ) : null}
      <DropdownMenuItem onSelect={() => requestAnimationFrame(requestBack)}>{t('studio.dock.leaveCollection')}</DropdownMenuItem>
    </>
  );
  const optionsMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Deliberately NOT an ellipsis/More icon: every orbiter already carries its own More button, and
            the ellipsis is this product's top-right menu pattern. A sliders/tune glyph reads as
            "settings for this view" instead. */}
        <CornerButton
          kind="kick"
          icon={<SlidersHorizontal />}
          label={t('studio.dock.options')}
          aria-label={t('studio.dock.optionsAria')}
          dropIndicator="up"
        />
      </DropdownMenuTrigger>
      {/* Portals to <body> (outside the studio's `dark` scope), so re-scope it dark to match the floating
          chrome; open ABOVE the bar. `.orb-studio__portal-surface` makes it read the USER chrome
          theme (body-mirrored `--orb-studio-chrome-*`), like the inspector's portalled menus. */}
      <DropdownMenuContent side="top" align="center" className="dark orb-studio__portal-surface">
        {/* The layout (deck count) is FIXED by the room shape at creation, so the count switcher is
            inert in room mode — changing it would fork this client's deck count from the room's. */}
        {!roomActive ? (
          <DropdownMenuRadioGroup
            value={String(subtype)}
            onValueChange={(value) => {
              const next = Number(value);
              // Defer past the menu's close: a shrink opens the confirm AlertDialog, and two Radix modals
              // transitioning in the same tick can leave the body pointer-events lock stuck.
              requestAnimationFrame(() => requestSubtype(next));
            }}
          >
            {[1, 2, 3, 4].map((count) => (
              <DropdownMenuRadioItem key={count} value={String(count)}>
                {count === 1 ? t('studio.dock.orbiterCountOne') : t('studio.dock.orbiterCountMany', { count })}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        ) : null}
        {!roomActive ? <DropdownMenuSeparator /> : null}
        {/* Fullscreen the WHOLE view — every orbiter and the chrome around them. Each orbiter's own More
            menu already fullscreens just ITS cell; this is the view-level counterpart, which is why it
            lives here and calls the shell toggle rather than the per-voice one. NOT deferred past the
            menu close like the dialog items: `requestFullscreen` needs the click's user gesture. */}
        <DropdownMenuItem onSelect={() => void toggleShellFullscreen()}>
          {t('topBar.moreMenu.fullscreen')}
        </DropdownMenuItem>
        {/* Without a header (an inline roster) the session-level actions stay here, so leaving is
            always reachable from somewhere. */}
        {headerTitle ? null : sessionActions}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // The header names the session and opens its menu — the session-level actions live there, beside
  // the name they act on, while Options keeps what is about this view.
  const header: ScoreHeaderTitle | null = headerTitle
    ? { name: headerTitle, menu: sessionActions, menuContentClassName: 'dark orb-studio__portal-surface' }
    : null;

  // Desktop and mobile share the SAME element tree (root → wrapper → gridRef → per-index blocks → stage
  // cells); only styles + conditional chrome differ by `isMobile`. Keeping element identity across the
  // breakpoint means a rotate never remounts a stage cell (which would strand the compositor's stored
  // cell ref). Desktop = absolute resizable grid + dividers + nav bar; mobile = vertical scroll stack
  // with per-stage footer (Roman + prev/next), no resize.
  //
  // The whole tree is wrapped in `IconProvider` so the design-lib `<Icon>` (the nav's orbiter glyph, the
  // card type badges) resolves its bytes from the SAME Herbarium CDN base as the rest of the app — this
  // studio is a SEPARATE React root (`mountMultiStageStudio`), so it isn't under OrbitersUI's provider.
  return (
    <IconProvider baseUrl={getHerbariumBase() || undefined}>
    <div
      ref={scrollContainerRef}
      // `dark` scopes the design-library tokens to dark mode — the studio floats over the black 3D scene,
      // so its lib chrome (nav / drawer / dialog / cards) themes for a dark surface.
      //
      // The `zIndex: 41` below is load-bearing in BOTH directions: it must stay above the shared realm
      // canvas (40) or the studio disappears behind the scene, and it must stay below the MIDI-learn
      // overlays (43, see MidiOverlayManager) or those overlays become unclickable — this layer is
      // fixed and full-viewport, so it swallows every learn click aimed at a control inside it.
      className={`dark ${isMobile ? 'multi-stage-studio-scroll' : ''}`}
      style={
        isMobile
          ? {
              position: 'fixed',
              inset: 0,
              zIndex: 41,
              pointerEvents: 'none',
              overflowY: 'auto',
              overflowX: 'hidden',
              // Start the scroll stack below the score bar, so the first stage's own top-right
              // controls are reachable rather than sitting under it.
              paddingTop: TOP_CHROME_INSET,
            }
          : {
              position: 'fixed',
              inset: 0,
              zIndex: 41,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              // Reserve the fixed BottomNavBar's strip so the grid + drawer sit above it (the bar pins its
              // own surface to the viewport bottom; this keeps the shared stage tree intact — no remount).
              paddingBottom: 'var(--nav-mobile-bar-height, 4rem)',
              // Same at the top edge for the room's score bar: the grid shrinks to fit under it, and
              // the compositor re-frames each voice from its stage rect, so the orbiter moves down
              // with its own chrome instead of being covered by the bar.
              paddingTop: TOP_CHROME_INSET,
            }
      }
    >
      {/* The stage area, and the only thing the local-files panel takes room from. Reserved rather
          than overlaid — the band the panel would cover holds each stage's own top-right controls.
          The DRAWER is a sibling below this and keeps the full width: the panel runs from the
          drawer's top edge upward, so the rail of cards stays as wide as the window. */}
      <div
        style={
          isMobile
            ? { padding: 0 }
            : { position: 'relative', flex: 1, minHeight: 0, padding: 4, paddingRight: `calc(4px + ${LOCAL_PANEL_INSET})` }
        }
      >
        <div
          ref={gridRef}
          data-orbiter-stage-grid=""
          style={
            isMobile
              ? { display: 'flex', flexDirection: 'column', gap: 0, minHeight: '100%' }
              : { position: 'relative', width: '100%', height: '100%', minHeight: 0 }
          }
        >
          {Array.from({ length: subtype }, (_, index) => {
            const isFilled = isStageFilled(index);
            const isDropTarget = dropStage === index || fileDropStage === index;
            const s = stageStyles[index] ?? { left: '0', top: '0', width: '100%', height: '100%' };
            return (
              <div
                key={index}
                ref={getBlockRefCb(index)}
                style={
                  isMobile
                    ? {
                        position: 'relative',
                        width: '100%',
                        flexShrink: 0,
                        height: MOBILE_STAGE_HEIGHT,
                        overflow: 'hidden',
                      }
                    : {
                        position: 'absolute',
                        left: s.left,
                        top: s.top,
                        width: s.width,
                        height: s.height,
                        overflow: 'hidden',
                        minWidth: 0,
                        minHeight: 0,
                        pointerEvents: 'none',
                        transition: resizing ? 'none' : SLOT_TRANSITION,
                      }
                }
              >
                {/* Compositor cell + voice UI mount (kept childless by this root). Leaves footer room on mobile.
                    `multi-orbiter-cell` makes this stage the orbiter UI's size query container (orbitersUI.css),
                    exactly like the auto-grid tile cell — so the orbiter's container-relative responsive system
                    (continuous cq scaling + the discrete width/height breakpoints) keys off the STAGE box here.
                    Without it the orbiter chrome (header, knobs, waveform) falls back to viewport sizing and the
                    mobile compaction never fires, overflowing a phone-width stage. */}
                <div
                  ref={getStageRefCb(index)}
                  className="multi-orbiter-cell"
                  onDragEnter={(e) => {
                    if (!localRail || isMobile || !dragCarriesFiles(e.dataTransfer)) return;
                    e.preventDefault();
                    setFileDropStage(index);
                  }}
                  onDragOver={(e) => {
                    if (!localRail || isMobile || !dragCarriesFiles(e.dataTransfer)) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'copy';
                    if (fileDropStage !== index) setFileDropStage(index);
                  }}
                  onDragLeave={(e) => {
                    if (!localRail || !dragCarriesFiles(e.dataTransfer)) return;
                    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                    setFileDropStage((current) => (current === index ? null : current));
                  }}
                  onDrop={(e) => {
                    if (!localRail || isMobile || !dragCarriesFiles(e.dataTransfer)) return;
                    e.preventDefault();
                    setFileDropStage(null);
                    localRail.onDropFiles(e.dataTransfer, index);
                  }}
                  // pointerEvents:auto — the stage cell is this voice's own camera-input surface,
                  // so its CameraController receives planet drags directly and DOM hit-testing focuses the
                  // right stage on pointerdown (no realm-level geometry hit-test, no fall-through to the
                  // shared canvas). The orbiter chrome inside opts back to :none per #orbiters-react-ui-root.
                  style={
                    isMobile
                      ? { ...mobileContentInset, pointerEvents: 'auto' }
                      : { position: 'absolute', inset: 0, pointerEvents: 'auto' }
                  }
                />

                {/* Who may drive this deck, on an EMPTY deck. A filled one carries the same faces
                    inside its own play UI (OrbitersUI); here there is no play UI to carry them, so
                    the two boxes that give a tile its size context stand in — see orbitersUI.css.
                    The question is the same either way: who may load a card here, and play it. */}
                {!isFilled && (
                  <div
                    className="orbiters-deck-frame"
                    style={isMobile ? mobileContentInset : undefined}
                  >
                    <div className="orbiters-deck-tokens">
                      <DeckHolderFaces wireId={String(index)} />
                    </div>
                  </div>
                )}

                {/* Empty stage: the design-lib FourCornerCard corner-bracket frame around a Roman numeral +
                    a "drop / double-tap to load" hint (shown only when arrange is available). The frame highlights
                    (accent corners) when a dragged card is over it. On mobile it occupies the SAME reduced
                    box as the orbiter canvas (above the bar + open drawer), never the full screen. */}
                {!isFilled && (
                  <div
                    className="orb-studio__portal-surface"
                    style={
                      isMobile
                        ? { ...mobileContentInset, pointerEvents: 'none', padding: 20 }
                        : { position: 'absolute', inset: 0, pointerEvents: 'none', padding: 20 }
                    }
                  >
                    <FourCornerCard
                      cornerColors={isDropTarget ? 'var(--color-foreground, #ffffff)' : 'color-mix(in oklab, var(--color-foreground, #ffffff) 35%, transparent)'}
                      cornerWidth={24}
                      cornerHeight={22}
                      // The frame marks the drop target, and the wrapper is pointer-events:none, so
                      // the card's default hover reveal would never fire — and a drag-over is not a
                      // hover in any case. These corners stay on.
                      alwaysShowCorners
                      // The frame spans the WHOLE placeholder (its corners sit at the stage edges, inset
                      // only by the wrapper padding) so the entire stage reads as the drop target — not a
                      // small box floating in the middle.
                      style={{ width: '100%', height: '100%' }}
                      contentClassName="flex h-full flex-col items-center justify-center"
                    >
                      <span style={{ fontSize: '2rem', color: 'var(--color-muted-foreground, rgba(255,255,255,0.45))' }}>{SLOT_LABELS[index] ?? ''}</span>
                      {canArrange && (
                        <span style={{ marginTop: 6, fontSize: '0.9rem', color: 'var(--color-muted-foreground, rgba(255,255,255,0.45))' }}>{t('studio.stage.emptyHint')}</span>
                      )}
                    </FourCornerCard>
                  </div>
                )}

                {/* Shell MIDI anchor for THIS position (select the slot): a learn-only chip —
                    zero-size until body.midi-learn-mode, so it costs nothing and the overlay
                    layer ignores it outside learn mode. Sits BELOW the orbiter's transport row
                    (a second row), never over it — the transport buttons are learn targets too. */}
                {shellMidiCollectionId ? (
                  <div
                    className="orb-studio__midi-anchors orb-studio__portal-surface"
                    // 116px clears the transport row PLUS its learn-mode CH/CC badges at the
                    // tallest header scale (a full-screen stage measured ~103px to badge
                    // bottom); smaller stages have a compacter header, so the chip just gains
                    // margin there instead of colliding.
                    //
                    // The MIDI-learn control row sits between the two and is present only while
                    // learning, so its height is ADDED rather than folded into the constant: the
                    // variable is 0 outside learn mode, which is exactly when the old number was
                    // measured. Without it this deck select would land underneath that row.
                    style={{
                      position: 'absolute',
                      top: 'calc(116px + var(--orb-midi-bar-height, 0px))',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 6,
                    }}
                  >
                    <span
                      ref={getShellAnchorRefCb(`collection-stage-focus-${index + 1}`)}
                      id={`pm-collection-stage-focus-${index + 1}`}
                      className="orb-studio__midi-anchor"
                    >
                      Select {SLOT_LABELS[index] ?? index + 1}
                    </span>
                  </div>
                ) : null}

                {/* Drag drop-target highlight over a FILLED stage under the pointer (the empty-stage frame
                    highlights itself, above). */}
                {isDropTarget && isFilled && (
                  <div
                    className="orb-studio__portal-surface"
                    style={{
                      position: 'absolute',
                      inset: 4,
                      borderRadius: 8,
                      border: '2px solid var(--color-border-2, rgba(255,255,255,0.85))',
                      background: 'color-mix(in oklab, var(--color-foreground, #ffffff) 6%, transparent)',
                      pointerEvents: 'none',
                      zIndex: 5,
                    }}
                  />
                )}

                {isFilled && stageEntries?.[index] ? (
                  <div
                    ref={getFocusArtworkRefCb(index)}
                    id={`pm-collection-focus-track-${index}`}
                    className="orb-studio__portal-surface"
                    style={{
                      position: 'absolute',
                      left: 12,
                      top: 12,
                      pointerEvents: 'none',
                      // Every stage size and corner tried (bottom-right, top-left, shrunk down)
                      // ended up overlapping some piece of the orbiter's own chrome (InteractionMenu,
                      // header transport, expanded track-info panel) at one screen size or another —
                      // there's no corner that's safe at every stage size. This is now HIDDEN everywhere
                      // and kept as an empty anchor only — it's still this track's MIDI-learn focus target
                      // (see the registration effect), which just needs the ref/id, not rendered artwork.
                      // The bottom-left A/B/C/D focus badge that used to render alongside it had the same
                      // overlap problem and was removed outright; onFocusStage is still reachable via
                      // other UI (drawer / bottom bar / MIDI), so no functionality is lost.
                      display: 'none',
                    }}
                    aria-hidden="true"
                  />
                ) : null}

                {/* Per-stage remove, top-right (clears the stage's voice back to a placeholder). Shown on
                    mobile too — each stage is a full screen there, so this is how you close the current
                    orbiter and free its slot. Available to anyone who can arrange. */}
                {isFilled && canArrange && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="orb-studio__portal-surface"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => onClearStage?.(index)}
                    aria-label={t('studio.stage.removeFromStage', { number: index + 1 })}
                    style={{
                      // A phantom (ghost) control — no border, no surface; just the glyph. The lib ghost
                      // Button supplies the hover affordance. Pinned into the EXTREME top-right corner (tiny
                      // margin) so it clears the orbiter's own header ⋯ menu, which owns the inset top-right.
                      position: 'absolute',
                      right: 2,
                      top: 2,
                      width: 30,
                      height: 30,
                      color: 'var(--color-foreground, #ffffff)',
                      pointerEvents: 'auto',
                    }}
                  >
                    <X />
                  </Button>
                )}

                {/* Mobile chrome (pager nav + Cards toggle) is a SINGLE global BottomNavBar rendered once
                    below, not per-stage — see the mobile dock near the desktop one. */}
              </div>
            );
          })}

          {!isMobile &&
            dividers.map((d) => (
              <div
                key={`${d.ratio}-${d.axis}`}
                className="orb-studio__portal-surface"
                data-orbiter-divider={d.ratio}
                onPointerDown={startDrag(d)}
                style={{
                  position: 'absolute',
                  left: d.style.left,
                  top: d.style.top,
                  width: d.style.width,
                  height: d.style.height,
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: d.axis === 'x' ? 'col-resize' : 'row-resize',
                  pointerEvents: 'auto',
                  touchAction: 'none',
                }}
              >
                <div
                  style={{
                    width: d.axis === 'x' ? 3 : 32,
                    height: d.axis === 'x' ? 32 : 3,
                    borderRadius: 9999,
                    background: resizing
                      ? 'color-mix(in oklab, var(--color-foreground, #ffffff) 28%, transparent)'
                      : 'color-mix(in oklab, var(--color-foreground, #ffffff) 22%, transparent)',
                    opacity: resizing ? 1 : 0.6,
                  }}
                />
              </div>
            ))}
        </div>
      </div>

      {/* MIDI-learn color legend (learn-mode only, like the chips): green targets save to the
          loaded track, blue ones to this collection. Pinned above the dock strip. */}
      {shellMidiCollectionId ? (
        <div
          className="orb-studio__midi-anchors orb-studio__portal-surface"
          style={{
            position: 'absolute',
            bottom: 'calc(var(--nav-mobile-bar-height, 4rem) + 12px)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 45,
          }}
        >
          <span className="orb-studio__midi-legend-entry" data-midi-scope="orbiter">
            Saved to track
          </span>
          <span className="orb-studio__midi-legend-entry" data-midi-scope="collection">
            Saved to collection
          </span>
        </div>
      ) : null}

      {/* Arrange drawer — DESKTOP (in-flow). Sits above the nav; its height eats into the grid area, so
          stages reflow (the compositor re-reads their rects each frame). */}
      {drawerMounted && (
        <CardDrawer
          cruise={cruise}
          loadDefaults={loadDefaults}
          midiScope={midiScope}
          entityFilters={entityFilters}
          offlineControl={offlineControl}
          open={drawerOpen}
          initialHeight={drawerHeight}
          onHeightSettled={handleDrawerHeightSettled}
          containerRef={chromeRef}
          entries={drawerEntries}
          activeVoiceId={stageVoiceIds?.[activeIndex] ?? null}
          draggingVoiceId={dragEntry?.voiceId ?? null}
          dropBeforeVoiceId={dropBeforeVoiceId}
          dropAtEnd={dropAtEnd}
          loadingSourceIds={loadingSourceIds}
          exitingSourceIds={exitingIds}
          dealId={dealId}
          localRail={localRail}
          onOpenLocalFiles={localRail?.enabled ? () => setLocalPanelShown(!localPanelOpen) : null}
          localFilesOpen={localPanelOpen}
          rowsDraggingOver={localDragOverRail}
          onCardPointerDown={onCardPointerDown}
          onCardMenuOpen={cancelCardDrag}
          onRemoveEntry={onRemoveEntry}
          removeEntryLabelKey={removeEntryLabelKey}
          registerCardRef={registerCardRef}
          registerEndRef={(node) => {
            endRef.current = node;
          }}
          registerScrollRef={(node) => {
            railScrollRef.current = node;
          }}
          onRequestClose={() => setDrawerShown(false)}
        />
      )}

      {/* Arrange drawer — MOBILE. A FIXED reduced-height rail (no resize) pinned above the nav: portrait
          along the bottom above the bar; landscape along the bottom to the RIGHT of the left rail. It does
          NOT overlay the orbiter — the stage box (`mobileContentInset`) shrinks by this drawer's height
          while it's open, so the cards and the full play area are both visible (the desktop reflow, adapted
          to the phone). pointer-events:none wrapper; the drawer re-enables its own. A drag STARTS on a card,
          so once armed the pointer stream is captured here and never reaches the camera. */}
      {isMobile && canShowDrawer && (
        <div
          style={{
            position: 'fixed',
            zIndex: 45,
            pointerEvents: 'none',
            ...(isLandscape
              ? { left: MOBILE_RAIL_W, right: 0, bottom: 0 }
              : { left: 0, right: 0, bottom: MOBILE_BAR_INSET }),
          }}
        >
          <CardDrawer
            cruise={cruise}
            loadDefaults={loadDefaults}
            midiScope={midiScope}
            entityFilters={entityFilters}
            open={drawerOpen}
            containerRef={chromeRef}
            // Mobile has no resize: the drawer is one fixed reduced height, so there is no height to
            // open at and none to report.
            fixedHeight={MOBILE_DRAWER_H}
            entries={drawerEntries}
            activeVoiceId={stageVoiceIds?.[activeIndex] ?? null}
            draggingVoiceId={dragEntry?.voiceId ?? null}
            dropBeforeVoiceId={dropBeforeVoiceId}
            dropAtEnd={dropAtEnd}
            loadingSourceIds={loadingSourceIds}
            exitingSourceIds={exitingIds}
            dealId={dealId}
            localRail={localRail}
          onOpenLocalFiles={localRail?.enabled ? () => setLocalPanelShown(!localPanelOpen) : null}
          localFilesOpen={localPanelOpen}
          rowsDraggingOver={localDragOverRail}
            onCardPointerDown={onCardPointerDown}
            onCardMenuOpen={cancelCardDrag}
            onRemoveEntry={onRemoveEntry}
            removeEntryLabelKey={removeEntryLabelKey}
            registerCardRef={registerCardRef}
            registerEndRef={(node) => {
              endRef.current = node;
            }}
            registerScrollRef={(node) => {
              railScrollRef.current = node;
            }}
            onRequestClose={() => setDrawerShown(false)}
          />
        </div>
      )}

      {/* Managing the files this device is holding. Desktop: a column down the right, which the
          studio root has RESERVED (see LOCAL_PANEL_INSET) so the stages reflow beside it and the
          compositor re-frames each voice from its own rect. Mobile: a bottom sheet, because a
          column on a phone is a column of nothing. Only where there is a local rail at all. */}
      {localRail?.enabled && (
        <LocalFilesPanel
          open={localPanelOpen}
          initialWidth={localPanelWidth}
          onWidthSettled={handleLocalPanelWidthSettled}
          onClose={() => setLocalPanelShown(false)}
          // The panel is CHROME, like the dock and the drawer: the studio writes the user's own
          // preset tokens onto it here, rather than it painting whatever happens to be ambient.
          containerRef={(node) => {
            localPanelRef.current = node;
            return chromeRef(node);
          }}
          onAddToCollection={(ids) => localRail.onAddToCollection(ids)}
          // The library's own resolved name first: it comes from the same lookup the local-files
          // page uses, so the panel and the page never call one collection two things. The
          // studio's prop is the fallback for a session that has not named it.
          collectionName={localRail.collectionName ?? collectionName}
          collectionKey={localRail.scopeKey}
          onRowsDragMove={onLocalRowsDragMove}
          onRowsDrop={onLocalRowsDrop}
          onRowsDragCancel={clearLocalDragTargets}
          isMobile={isMobile}
          topInset={TOP_CHROME_INSET}
          // Above the dock AND above the drawer: the drawer is the full-width surface along the
          // bottom, and this column rises from its top edge. `--orbiters-drawer-height` is the
          // drawer's own published strip, so nothing here restates a height it does not own.
          bottomInset={`calc(${MOBILE_BAR_INSET} + var(--orbiters-drawer-height, 0px))`}
          // Portrait the dock is a bottom bar, landscape it is a left rail — the sheet clears
          // whichever one is there, from the same fact the stage box already reflows around.
          mobileInset={
            isLandscape
              ? { left: MOBILE_RAIL_W, right: 0, bottom: 0 }
              : { left: 0, right: 0, bottom: MOBILE_BAR_INSET }
          }
        />
      )}

      {/* Persistent bottom dock — the SAME model on mobile and desktop now: prev/next orbiter · the
          active-orbiter monitor (which also opens the orbiter MAP) · Cards, with Options pinned to the
          RIGHT. Monitor and Options sit this way round on purpose: the readout you consult
          mid-play stays near the pager, and the settings menu ends the bar. The only per-platform
          difference is the pager glyphs (mobile pages full-screen stages vertically → up/down; desktop
          selects across the grid → left/right) and the landscape RAIL orientation. Leaving lives in the
          header's menu (Options when there is no header), so the bar carries only orbiter controls.
          `multi-stage-studio-dock` overrides `--foreground` to white so the dock reads light-on-dark. */}
      <StudioHeader
        title={header}
        roomActive={roomActive}
        left={isLandscape ? MOBILE_RAIL_W : 0}
        chromeRef={chromeRef}
      />
      {isMobile && (
        <BottomNavBar
          ref={chromeRef}
          orientation={isLandscape ? 'rail' : 'bottom'}
          surface="glass"
          className="multi-stage-studio-dock"
          style={{ pointerEvents: 'auto' }}
        >
          {/* The pager + Cards buttons ARE the shell MIDI targets (id + ref feed the
              registration effect) — in learn mode they get the standard overlay treatment,
              tinted per the collection scope, exactly like every other mappable control. */}
          <CornerButton
            kind="kick"
            id="pm-collection-stage-prev"
            ref={getShellAnchorRefCb('collection-stage-prev')}
            icon={<ArrowUp />}
            label={t('studio.dock.prev')}
            aria-label={t('studio.dock.prevAria')}
            disabled={pagerPrevIndex == null}
            onClick={() => navigateToStage(pagerPrevIndex)}
          />
          <CornerButton
            kind="kick"
            id="pm-collection-stage-next"
            ref={getShellAnchorRefCb('collection-stage-next')}
            icon={<ArrowDown />}
            label={t('studio.dock.next')}
            aria-label={t('studio.dock.nextAria')}
            disabled={pagerNextIndex == null}
            onClick={() => navigateToStage(pagerNextIndex)}
          />
          {trackMonitor}
          {canShowDrawer && (
            <CornerButton
              kind="toggle"
              id="pm-collection-drawer-toggle"
              ref={getShellAnchorRefCb('collection-drawer-toggle')}
              pressed={drawerOpen}
              icon={drawerOpen ? <ChevronDown /> : <ChevronUp />}
              label={t('studio.dock.cards')}
              aria-label={drawerOpen ? t('studio.dock.hideCards') : t('studio.dock.showCards')}
              onPressedChange={() => setDrawerShown(!drawerOpen)}
            />
          )}
          {optionsMenu}
        </BottomNavBar>
      )}

      {!isMobile && (
        <BottomNavBar
          ref={chromeRef}
          orientation="bottom"
          surface="glass"
          className="multi-stage-studio-dock"
          style={{ pointerEvents: 'auto' }}
        >
          {/* Same shell-MIDI-target wiring as the mobile bar (only one bar mounts at a time,
              so the ids stay unique; an isMobile flip re-registers via the effect deps). */}
          <CornerButton
            kind="kick"
            id="pm-collection-stage-prev"
            ref={getShellAnchorRefCb('collection-stage-prev')}
            icon={<ArrowLeft />}
            label={t('studio.dock.prev')}
            aria-label={t('studio.dock.prevAria')}
            disabled={pagerPrevIndex == null}
            onClick={() => navigateToStage(pagerPrevIndex)}
          />
          <CornerButton
            kind="kick"
            id="pm-collection-stage-next"
            ref={getShellAnchorRefCb('collection-stage-next')}
            icon={<ArrowRight />}
            label={t('studio.dock.next')}
            aria-label={t('studio.dock.nextAria')}
            disabled={pagerNextIndex == null}
            onClick={() => navigateToStage(pagerNextIndex)}
          />
          {trackMonitor}
          {canArrange && (
            <CornerButton
              kind="toggle"
              id="pm-collection-drawer-toggle"
              ref={getShellAnchorRefCb('collection-drawer-toggle')}
              pressed={drawerOpen}
              // The chevron already flips to signal open/closed, so no separate dropIndicator caret.
              icon={drawerOpen ? <ChevronDown /> : <ChevronUp />}
              label={t('studio.dock.cards')}
              aria-label={drawerOpen ? t('studio.dock.hideCards') : t('studio.dock.showCards')}
              onPressedChange={() => setDrawerShown(!drawerOpen)}
            />
          )}
          {optionsMenu}
        </BottomNavBar>
      )}

      {/* Drag overlay — a compact copy of the ported card following the pointer (root's DraggableCardOverlay
          renders the same StationTrackCard). The ref'd wrapper is positioned imperatively (onCardPointerDown). */}
      {dragEntry && (
        <div
          ref={overlayRef}
          className="orb-studio__portal-surface"
          style={{ position: 'fixed', zIndex: 60, pointerEvents: 'none', width: 260, height: 200, opacity: 0.95, left: dragPosRef.current.x + 12, top: dragPosRef.current.y + 12 }}
        >
          <StationTrackCard entry={dragEntry} compact showBadge />
        </div>
      )}

      {/* Nothing in this collection can start a stage (design-lib AlertDialog, single action) — said on
          arrival, because every empty stage will otherwise just refuse each card without explaining. */}
      <AlertDialog
        open={noPlayableEntries && !noticeDismissed}
        onOpenChange={(next) => {
          if (!next) setNoticeDismissed(true);
        }}
      >
        <AlertDialogContent className="dark orb-studio__portal-surface">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('studio.notPlayable.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('studio.notPlayable.body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setNoticeDismissed(true)}>
              {t('studio.notPlayable.dismiss')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation dialog (design-lib AlertDialog) — a count-shrink that would drop loaded orbiters, or
          leaving the collection. */}
      <AlertDialog
        open={pendingConfirmation !== null}
        onOpenChange={(next) => {
          if (!next) setPendingConfirmation(null);
        }}
      >
        <AlertDialogContent className="dark orb-studio__portal-surface">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingConfirmation?.kind === 'back' ? t('studio.confirm.leaveTitle') : t('studio.confirm.shrinkTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingConfirmation?.kind === 'back'
                ? t('studio.confirm.leaveBody')
                : pendingConfirmation
                  ? pendingConfirmation.dropped === 1
                    ? t('studio.confirm.shrinkBodyOne')
                    : t('studio.confirm.shrinkBodyMany', { count: pendingConfirmation.dropped })
                  : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingConfirmation(null)}>{t('studio.confirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmProceed}>{t('studio.confirm.continue')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* In-app "Start room": mint a room grant for this collection and hand back the share + creator
          links. Only mounted for a collection session that isn't already a room. */}
      {canStartRoom && collectionId ? (
        <StartRoomDialog open={startRoomOpen} onOpenChange={setStartRoomOpen} collectionId={collectionId} t={t} />
      ) : null}

      {/* The local audio rail's one dialog: the missing-files list, driven by the controller's props. */}
      {localRail ? <LocalMissingDialog rail={localRail} t={t} /> : null}
    </div>
    </IconProvider>
  );
}

export interface MountedStudio {
  host: HTMLElement;
  root: Root;
  /** Resolves with the imperative handle once the initial stages are committed. */
  ready: Promise<MultiStageStudioHandle>;
  /** Merge new props and re-render (the component's own state — subtype, splits, drag — is preserved
   *  across re-renders). Used to push the live arrangement (`stageVoiceIds`, `entries`) from the realm. */
  update: (next: Partial<Omit<MultiStageStudioProps, 'onReady'>>) => void;
  dispose: () => void;
}

/** Mount the multi-stage Studio into a fresh full-screen host and return an imperative handle promise. */
export function mountMultiStageStudio(
  props: Omit<MultiStageStudioProps, 'onReady'>,
): MountedStudio {
  const host = document.createElement('div');
  host.id = 'multi-stage-studio-root';
  document.body.appendChild(host);
  const root = createRoot(host);

  let resolveReady: (handle: MultiStageStudioHandle) => void;
  const ready = new Promise<MultiStageStudioHandle>((resolve) => {
    resolveReady = resolve;
  });

  let current = { ...props };
  const render = () =>
    root.render(<MultiStageStudio {...current} onReady={(handle) => resolveReady(handle)} />);
  render();

  return {
    host,
    root,
    ready,
    update: (next) => {
      current = { ...current, ...next };
      render();
    },
    dispose: () => {
      root.unmount();
      host.remove();
    },
  };
}
